<template>
  <div class="cardapio-online-admin">
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Cardápio Online
          </span>
          <span class="text-muted mt-2 font-weight-bold font-size-sm">
            Gestão do site do cardápio
          </span>
        </h3>
        <div class="card-toolbar">
          <button
            type="button"
            class="btn btn-light-primary font-weight-bolder font-size-sm mr-2"
            :disabled="isSaving('navigation')"
            @click="goToNewProduct"
          >
            Cadastrar produto
          </button>
          <button
            type="button"
            class="btn btn-light-dark font-weight-bolder font-size-sm mr-2"
            @click="goToProducts"
          >
            Produtos
          </button>
          <button
            type="button"
            class="btn btn-light-warning font-weight-bolder font-size-sm mr-2"
            @click="openPublicPreview"
          >
            Ver site
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <div class="presentation-alert mb-8">
          <div class="presentation-alert-icon">
            <i class="flaticon2-check-mark"></i>
          </div>
          <div>
            <strong>Pronto para a operação.</strong>
            <p>
              Controle aqui o que o cliente vê no cardápio: loja aberta,
              produtos, fotos, entrega, retirada, pagamento e mensagens. Valores
              exibidos são conferidos pelo sistema ao finalizar o pedido.
            </p>
          </div>
        </div>

        <div class="overview-grid mb-8">
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="overview-card"
          >
            <span>{{ metric.label }}</span>
            <strong>{{ metric.value }}</strong>
            <small>{{ metric.hint }}</small>
          </div>
        </div>

        <div class="admin-layout">
          <aside class="admin-sidebar">
            <button
              v-for="section in sections"
              :key="section.id"
              type="button"
              class="section-button"
              :class="{ 'section-button-active': activeSection === section.id }"
              @click="selectSection(section.id)"
            >
              <strong>{{ section.label }}</strong>
              <span>{{ section.description }}</span>
            </button>
          </aside>

          <main class="admin-content">
            <div class="section-header">
              <div>
                <span class="text-muted font-weight-bold font-size-sm">
                  {{ currentSection.kicker }}
                </span>
                <h4>{{ currentSection.label }}</h4>
                <p>{{ currentSection.longDescription }}</p>
              </div>
              <button
                type="button"
                class="btn btn-primary font-weight-bold"
                @click="openPrimaryAction"
              >
                {{ currentSection.actionLabel }}
              </button>
            </div>

            <section v-if="managementLoading" class="loading-box">
              Carregando informações do cardápio...
            </section>

            <section
              v-else-if="activeSection === 'overview'"
              class="content-grid"
            >
              <div class="control-card control-card-wide">
                <div class="brand-preview">
                  <img :src="logoUrl" alt="Logo" />
                  <div>
                    <h5>{{ config.name }}</h5>
                    <p>{{ config.description }}</p>
                    <strong>{{ config.publicUrl }}</strong>
                  </div>
                </div>
              </div>

              <div class="control-card">
                <h5>Status do site</h5>
                <strong
                  :class="config.habilitado ? 'text-success' : 'text-danger'"
                >
                  {{ config.habilitado ? "Publicado" : "Pausado" }}
                </strong>
                <p>{{ config.scheduleLabel }}</p>
                <small>{{ nextChangeLabel }}</small>
              </div>

              <div class="control-card">
                <h5>Pendências antes da publicação</h5>
                <ul>
                  <li v-for="alert in pendingAlerts" :key="alert">
                    {{ alert }}
                  </li>
                </ul>
              </div>

              <div class="control-card control-card-wide">
                <h5>O que esta sob controle</h5>
                <div class="surface-list">
                  <span
                    v-for="item in publicSurface"
                    :key="item"
                    class="label label-lg label-light-primary label-inline"
                  >
                    {{ item }}
                  </span>
                </div>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'publishing'"
              class="content-grid"
            >
              <div class="control-card">
                <h5>Identidade do restaurante</h5>
                <p>{{ config.name }}</p>
                <ul>
                  <li>Link: {{ config.publicUrl }}</li>
                  <li>Slug: {{ config.slug }}</li>
                  <li>{{ config.description }}</li>
                </ul>
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary font-weight-bold"
                  @click="openPublishingModal"
                >
                  Editar publicação
                </button>
              </div>

              <div class="control-card">
                <div class="control-card-top">
                  <h5>Status do cardápio</h5>
                  <span
                    class="label label-inline"
                    :class="publicationStatusClass"
                  >
                    {{ publicationStatusLabel }}
                  </span>
                </div>
                <p>{{ publicationStatusDescription }}</p>
                <ul>
                  <li v-for="check in publicationChecks" :key="check">
                    {{ check }}
                  </li>
                </ul>
                <div class="publishing-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-light-warning font-weight-bold"
                    @click="openPublicPreview"
                  >
                    Ver site
                  </button>
                  <button
                    v-if="!config.habilitado"
                    type="button"
                    class="btn btn-sm btn-success font-weight-bold"
                    :disabled="isSaving('config') || !canPublishCardapio"
                    @click="publishCardapio"
                  >
                    {{
                      isSaving("config") ? "Salvando..." : "Publicar cardápio"
                    }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-sm btn-light-danger font-weight-bold"
                    :disabled="isSaving('config')"
                    @click="openCardapioPauseModal"
                  >
                    Pausar cardápio
                  </button>
                </div>
              </div>

              <div class="control-card">
                <h5>Horários e aviso</h5>
                <p>{{ config.scheduleLabel }}</p>
                <small>{{ config.closedMessage }}</small>
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary font-weight-bold mt-4"
                  @click="openPublishingModal"
                >
                  Editar horários e mensagem
                </button>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'catalog'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Produtos do site</h5>
                  <p>
                    Produtos marcados para restaurante aparecem aqui. Para dados
                    fiscais, codigo e tributacao, use o cadastro oficial.
                  </p>
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold mr-2"
                    @click="goToNewProduct"
                  >
                    Cadastrar produto
                  </button>
                  <button
                    type="button"
                    class="btn btn-light-dark font-weight-bold"
                    @click="goToProducts"
                  >
                    Abrir produtos
                  </button>
                </div>
              </div>

              <div class="category-row">
                <div
                  v-for="category in categories"
                  :key="category.id"
                  class="category-chip"
                  :class="{ 'category-chip--paused': !category.available }"
                >
                  <strong>{{ category.name }}</strong>
                  <span>{{ category.count }} itens</span>
                  <small>
                    {{ category.available ? "Disponivel" : "Pausada" }}
                  </small>
                  <div class="catalog-status-actions">
                    <button
                      v-if="category.available"
                      type="button"
                      class="btn btn-xs btn-light-danger font-weight-bold"
                      :disabled="isSaving('category-status') || !category.id"
                      @click="openPauseModal('category', category)"
                    >
                      Pausar venda
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn btn-xs btn-light-success font-weight-bold"
                      :disabled="isSaving('category-status') || !category.id"
                      @click="activateCategory(category)"
                    >
                      Ativar
                    </button>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-head-custom table-vertical-center">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Categoria</th>
                      <th>Preço no site</th>
                      <th>Preparo</th>
                      <th>Status</th>
                      <th class="text-right">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in products" :key="product.slug">
                      <td>
                        <div class="product-cell">
                          <img
                            v-if="product.imageUrl"
                            :src="product.imageUrl"
                            :alt="product.name"
                          />
                          <span v-else>{{ product.initials }}</span>
                          <div>
                            <strong>{{ product.name }}</strong>
                            <small>{{ product.description }}</small>
                          </div>
                        </div>
                      </td>
                      <td>{{ categoryName(product.categoryId) }}</td>
                      <td>{{ formatMoney(product.priceCents) }}</td>
                      <td>
                        {{ product.noPreparation ? "Sem preparo" : "Cozinha" }}
                      </td>
                      <td>
                        <span
                          class="label label-lg label-inline"
                          :class="
                            product.available
                              ? 'label-light-success'
                              : 'label-light-danger'
                          "
                        >
                          {{ product.available ? "Disponivel" : "Pausado" }}
                        </span>
                      </td>
                      <td class="text-right">
                        <button
                          type="button"
                          class="btn btn-sm btn-light font-weight-bold mr-2"
                          :disabled="isSaving('product-status') || !product.id"
                          @click="
                            product.available
                              ? openPauseModal('product', product)
                              : activateProduct(product)
                          "
                        >
                          {{
                            !product.id
                              ? "Publicar primeiro"
                              : isSaving("product-status")
                              ? "Salvando..."
                              : product.available
                              ? "Pausar venda"
                              : "Ativar"
                          }}
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-light-primary font-weight-bold"
                          @click="openProductModal(product)"
                        >
                          Ajustar site
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'options'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Grupos globais</h5>
                  <p>
                    Use quando a mesma escolha deve aparecer em todo o cardápio
                    ou em categorias inteiras. Para borda, adicionais e
                    acompanhamentos de um item específico, abra o produto no
                    catálogo.
                  </p>
                </div>
                <button
                  type="button"
                  class="btn btn-light-primary font-weight-bold"
                  @click="openOptionProductPicker"
                >
                  Criar opção em produto
                </button>
              </div>

              <div class="option-scope-guide">
                <article>
                  <span>Global</span>
                  <strong>Vale para vários produtos</strong>
                  <p>
                    Bom para molhos, talheres, sacola, observações ou escolhas
                    que toda categoria compartilha.
                  </p>
                </article>
                <article>
                  <span>Produto</span>
                  <strong>Fica dentro de um item</strong>
                  <p>
                    Bom para borda de uma pizza, ponto da carne, remoção de
                    ingrediente ou adicional daquele produto.
                  </p>
                </article>
              </div>

              <div v-if="!globalOptionGroups.length" class="loading-box">
                Nenhum grupo de opções cadastrado.
              </div>

              <div v-else class="option-admin-list">
                <article
                  v-for="group in globalOptionGroups"
                  :key="group.id"
                  class="option-admin-card"
                >
                  <div>
                    <strong>{{ group.nome }}</strong>
                    <span>{{ optionGroupScopeLabel(group) }}</span>
                    <small>
                      {{ optionTypeLabel(group.tipo) }} · mínimo
                      {{ group.minimo }} · máximo {{ group.maximo || "livre" }}
                    </small>
                  </div>
                  <div class="option-admin-items">
                    <span
                      v-for="option in group.opcoes"
                      :key="option.id"
                      class="label label-inline"
                      :class="
                        option.available
                          ? 'label-light-primary'
                          : 'label-light-danger'
                      "
                    >
                      {{ option.nome }}
                      <template v-if="option.preco_adicional_centavos">
                        · {{ formatMoney(option.preco_adicional_centavos) }}
                      </template>
                      <button
                        v-if="option.available"
                        type="button"
                        class="btn btn-xs btn-clean text-danger ml-2 p-0"
                        :disabled="isSaving('option-item-status')"
                        @click="openPauseModal('option', option)"
                      >
                        Pausar
                      </button>
                      <button
                        v-if="!option.available"
                        type="button"
                        class="btn btn-xs btn-clean text-success ml-2 p-0"
                        :disabled="isSaving('option-item-status')"
                        @click="activateOptionItem(option)"
                      >
                        Ativar
                      </button>
                    </span>
                  </div>
                  <div class="option-admin-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openOptionGroupModal(group)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-dark font-weight-bold"
                      @click="duplicateOptionGroup(group)"
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold"
                      :class="
                        group.ativo ? 'btn-light-danger' : 'btn-light-success'
                      "
                      @click="toggleOptionGroup(group)"
                    >
                      {{ group.ativo ? "Pausar" : "Ativar" }}
                    </button>
                  </div>
                </article>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'pizzas'"
              class="content-block"
            >
              <div class="toolbar-line">
                <div>
                  <p>
                    Configure produtos que precisam de tamanho e sabores antes
                    de ir para o carrinho. Massa, borda e adicionais ficam em
                    Opções e adicionais.
                  </p>
                </div>
              </div>

              <div v-if="!pizzas.length" class="loading-box">
                Nenhuma variante cadastrada.
              </div>

              <div v-else class="option-admin-list">
                <article
                  v-for="pizza in pizzas"
                  :key="pizza.id"
                  class="option-admin-card"
                >
                  <div>
                    <strong>{{ pizza.nome_template }}</strong>
                    <span>{{ productName(pizza.cardapio_produto_id) }}</span>
                    <small>
                      {{ pizza.tamanhos.length }} tamanhos ·
                      {{ pizza.sabores.length }} sabores · adicional por sabor
                    </small>
                  </div>
                  <div class="option-admin-items">
                    <span
                      v-for="size in pizza.tamanhos"
                      :key="size.id || size.localKey"
                      class="label label-inline label-light-primary"
                    >
                      {{ size.nome }} · até {{ size.max_sabores }} sabores
                    </span>
                  </div>
                  <div class="option-admin-actions">
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openPizzaModal(pizza)"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-dark font-weight-bold"
                      @click="duplicatePizza(pizza)"
                    >
                      Duplicar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm font-weight-bold"
                      :class="
                        pizza.ativo ? 'btn-light-danger' : 'btn-light-success'
                      "
                      @click="togglePizza(pizza)"
                    >
                      {{ pizza.ativo ? "Pausar" : "Ativar" }}
                    </button>
                  </div>
                </article>
              </div>
            </section>

            <section
              v-else-if="activeSection === 'promotions'"
              class="content-block commercial-section"
            >
              <div class="toolbar-line">
                <div>
                  <h5>Vitrine comercial</h5>
                  <p>
                    Crie campanhas visíveis no cardápio, cupons de desconto e
                    combos com preço fechado.
                  </p>
                </div>
                <div class="toolbar-actions">
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="openPromotionModal()"
                  >
                    Nova promoção
                  </button>
                  <button
                    type="button"
                    class="btn btn-light-primary font-weight-bold"
                    @click="openCouponModal()"
                  >
                    Novo cupom
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary font-weight-bold"
                    @click="openComboModal()"
                  >
                    Novo combo
                  </button>
                </div>
              </div>

              <div class="commercial-grid">
                <article class="commercial-card commercial-card-featured">
                  <div>
                    <span>Promoções</span>
                    <strong>{{ promocoes.length }}</strong>
                    <small>Campanhas com período, produto e desconto.</small>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openPromotionModal()"
                  >
                    Configurar
                  </button>
                </article>
                <article class="commercial-card">
                  <div>
                    <span>Cupons</span>
                    <strong>{{ cupons.length }}</strong>
                    <small>Códigos validados no checkout.</small>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openCouponModal()"
                  >
                    Configurar
                  </button>
                </article>
                <article class="commercial-card">
                  <div>
                    <span>Combos</span>
                    <strong>{{ combos.length }}</strong>
                    <small>Produtos com composição e preço promocional.</small>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-light-primary font-weight-bold"
                    @click="openComboModal()"
                  >
                    Configurar
                  </button>
                </article>
              </div>

              <div class="commercial-list-grid">
                <div class="commercial-list">
                  <h5>Promoções ativas</h5>
                  <div v-if="!promocoes.length" class="loading-box">
                    Nenhuma promoção cadastrada.
                  </div>
                  <article
                    v-for="promotion in promocoes"
                    :key="promotion.id"
                    class="commercial-row"
                    :class="{
                      'commercial-row-with-image':
                        promotion.imagem_url || promotion.regras?.imagem_url,
                    }"
                  >
                    <img
                      v-if="
                        promotion.imagem_url || promotion.regras?.imagem_url
                      "
                      class="commercial-row-image"
                      :src="promotion.imagem_url || promotion.regras.imagem_url"
                      alt=""
                    />
                    <div>
                      <strong>{{ promotion.nome }}</strong>
                      <small>
                        {{ promotionTargetLabel(promotion) }} ·
                        {{ promotionPeriodLabel(promotion) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        promotion.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ promotion.ativo ? "Ativa" : "Pausada" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openPromotionModal(promotion)"
                    >
                      Editar
                    </button>
                  </article>
                </div>

                <div class="commercial-list">
                  <h5>Cupons</h5>
                  <div v-if="!cupons.length" class="loading-box">
                    Nenhum cupom cadastrado.
                  </div>
                  <article
                    v-for="coupon in cupons"
                    :key="coupon.id"
                    class="commercial-row"
                  >
                    <div>
                      <strong>{{ coupon.codigo }}</strong>
                      <small>
                        {{ coupon.nome || "Cupom de desconto" }} · mínimo
                        {{ formatMoney(coupon.pedido_minimo_centavos) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        coupon.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ coupon.ativo ? "Ativo" : "Pausado" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openCouponModal(coupon)"
                    >
                      Editar
                    </button>
                  </article>
                </div>

                <div class="commercial-list commercial-list-wide">
                  <h5>Combos</h5>
                  <div v-if="!combos.length" class="loading-box">
                    Nenhum combo cadastrado.
                  </div>
                  <article
                    v-for="combo in combos"
                    :key="combo.id"
                    class="commercial-row"
                  >
                    <div>
                      <strong>{{ combo.nome }}</strong>
                      <small>
                        {{ productName(combo.cardapio_produto_id) }} ·
                        {{ formatMoney(combo.preco_combo_centavos) }}
                      </small>
                    </div>
                    <span
                      class="label label-inline"
                      :class="
                        combo.ativo
                          ? 'label-light-success'
                          : 'label-light-danger'
                      "
                    >
                      {{ combo.ativo ? "Ativo" : "Pausado" }}
                    </span>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary font-weight-bold"
                      @click="openComboModal(combo)"
                    >
                      Editar
                    </button>
                  </article>
                </div>
              </div>
            </section>

            <section v-else class="content-grid">
              <div
                v-for="card in currentCards"
                :key="card.title"
                class="control-card"
              >
                <div class="control-card-top">
                  <h5>{{ card.title }}</h5>
                  <span class="label label-inline" :class="card.labelClass">
                    {{ card.status }}
                  </span>
                </div>
                <p>{{ card.description }}</p>
                <ul>
                  <li v-for="item in card.items" :key="item">{{ item }}</li>
                </ul>
                <button
                  type="button"
                  class="btn btn-sm btn-light-primary font-weight-bold"
                  @click="handleCardAction(card.action)"
                >
                  {{ card.buttonLabel }}
                </button>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>

    <b-modal
      id="modalCardapioPublishing"
      title="Publicação do cardápio"
      size="lg"
      hide-footer
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Nome exibido no site</label>
          <input v-model="publishingForm.name" class="form-control" />
          <small>Nome que aparece no topo do cardapio.</small>
          <small v-if="fieldError('nome_publico')" class="text-danger">
            {{ fieldError("nome_publico") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Slug do link</label>
          <input v-model="publishingForm.slug" class="form-control" />
          <small>Ex.: restaurante-demo.</small>
          <small v-if="fieldError('slug')" class="text-danger">
            {{ fieldError("slug") }}
          </small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição curta</label>
          <textarea
            v-model="publishingForm.description"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Texto curto para orientar o cliente.</small>
        </div>
        <div class="modal-control-field">
          <label>Horário exibido</label>
          <input v-model="publishingForm.scheduleLabel" class="form-control" />
          <small>Ex.: Hoje, 18:00 as 23:30.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Mensagem de loja fechada</label>
          <textarea
            v-model="publishingForm.closedMessage"
            class="form-control"
            rows="2"
          ></textarea>
          <small
            >Mensagem simples para quando o cardapio estiver pausado.</small
          >
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPublishing')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('config')"
          @click="savePublishing"
        >
          {{ isSaving("config") ? "Salvando..." : "Salvar publicação" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioPause" title="Pausar venda" size="md" hide-footer>
      <div class="pause-modal-body">
        <div class="pause-target-box">
          <span>{{ pauseForm.typeLabel }}</span>
          <strong>{{ pauseForm.name }}</strong>
          <small>
            Ao pausar, o cliente deixa de comprar este item até a venda ser
            reativada{{
              pauseForm.type === "cardapio"
                ? "."
                : " ou até o horário escolhido."
            }}
          </small>
        </div>

        <label
          v-for="option in visiblePauseOptions"
          :key="option.value"
          class="pause-choice"
          :class="{ 'pause-choice-active': pauseForm.mode === option.value }"
        >
          <input v-model="pauseForm.mode" type="radio" :value="option.value" />
          <span>
            <strong>{{ option.label }}</strong>
            <small>{{ option.description }}</small>
          </span>
        </label>

        <div v-if="pauseForm.mode === 'custom'" class="modal-control-field">
          <label>Data e hora para reativar</label>
          <input
            v-model="pauseForm.customUntil"
            class="form-control"
            type="datetime-local"
          />
          <small>Depois desse horário, a venda volta automaticamente.</small>
        </div>

        <div v-if="pauseForm.type === 'cardapio'" class="modal-control-field">
          <label>Motivo interno</label>
          <input
            v-model="pauseForm.reason"
            class="form-control"
            maxlength="120"
            placeholder="Ex.: loja fechada, manutenção, falta de equipe"
          />
          <small
            >Ajuda a equipe a entender por que o cardápio foi pausado.</small
          >
        </div>

        <div v-if="pauseForm.type === 'cardapio'" class="modal-control-field">
          <label>Mensagem para o cliente</label>
          <textarea
            v-model="pauseForm.message"
            class="form-control"
            rows="3"
            maxlength="255"
          ></textarea>
          <small
            >Texto exibido no site enquanto o cardápio estiver pausado.</small
          >
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPause')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-danger font-weight-bold"
          :disabled="isSaving(pauseForm.savingKey)"
          @click="confirmPauseTarget"
        >
          {{ isSaving(pauseForm.savingKey) ? "Salvando..." : "Pausar venda" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioDelivery"
      title="Entrega"
      size="lg"
      hide-footer
      @shown="renderDeliveryMaps"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field modal-control-field-wide">
          <div class="advanced-section-note">
            <strong v-if="deliveryForm.useGoogleMaps">
              Cobrança por distância (Google Maps)
            </strong>
            <strong v-else>Taxa única de entrega</strong>
            <span v-if="deliveryForm.useGoogleMaps">
              A taxa e o tempo são calculados pela distância até o endereço do
              cliente, dentro do raio configurado. Exige a chave do Google e o
              endereço base preenchidos abaixo.
            </span>
            <span v-else>
              Todos os pedidos de entrega usam a taxa e o pedido mínimo abaixo,
              sem depender de bairro ou distância. Ative o Google Maps somente
              se quiser cobrar por distância/raio.
            </span>
          </div>
        </div>
        <div class="modal-control-field">
          <label>Taxa de entrega</label>
          <money
            v-model="deliveryForm.feeText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Ex.: R$ 7,00.</small>
          <small
            v-if="fieldError('taxa_entrega_padrao_centavos')"
            class="text-danger"
          >
            {{ fieldError("taxa_entrega_padrao_centavos") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Pedido minimo para entrega</label>
          <money
            v-model="deliveryForm.minimumText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Ex.: R$ 25,00.</small>
          <small
            v-if="fieldError('pedido_minimo_centavos')"
            class="text-danger"
          >
            {{ fieldError("pedido_minimo_centavos") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Tempo minimo</label>
          <input
            v-model.number="deliveryForm.minMinutes"
            class="form-control"
            type="number"
          />
          <small>Tempo minimo em minutos.</small>
        </div>
        <div class="modal-control-field">
          <label>Tempo maximo</label>
          <input
            v-model.number="deliveryForm.maxMinutes"
            class="form-control"
            type="number"
          />
          <small>Tempo maximo em minutos.</small>
        </div>
        <div class="modal-control-field">
          <label>Entrega ativa</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="deliveryForm.deliveryEnabled" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Mostra entrega como opcao no checkout.</small>
        </div>
        <div class="modal-control-field">
          <label>Integrar com Google API</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="deliveryForm.useGoogleMaps" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small
            >Ative somente quando o restaurante for usar chave Google para
            calcular distância e raio.</small
          >
        </div>
        <div
          v-if="deliveryForm.useGoogleMaps"
          class="modal-control-field modal-control-field-wide"
        >
          <label>Chave Google para cálculo</label>
          <input
            v-model="deliveryForm.googleMapsApiKey"
            class="form-control"
            type="password"
            autocomplete="new-password"
            :placeholder="
              deliveryForm.googleMapsApiKeyConfigured
                ? 'Chave salva. Preencha apenas para trocar.'
                : 'Cole a chave server-side do Google Maps'
            "
          />
          <small>
            Usada pelo backend para Geocoding e Distance Matrix. Se ficar vazia,
            o sistema usa a chave padrão da plataforma quando houver.
          </small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Chave pública do mapa</label>
          <input
            v-model="deliveryForm.googleMapsBrowserKey"
            class="form-control"
            autocomplete="off"
          />
          <small>Usada para o mapa visual da gestão e do checkout.</small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Map ID</label>
          <input
            v-model="deliveryForm.googleMapsMapId"
            class="form-control"
            autocomplete="off"
          />
          <small
            >Opcional. Use quando houver mapa estilizado no Google Cloud.</small
          >
        </div>
        <div
          v-if="deliveryForm.useGoogleMaps"
          class="modal-control-field modal-control-field-wide"
        >
          <label>Endereço base do restaurante</label>
          <input v-model="deliveryForm.baseAddress" class="form-control" />
          <small>Ex.: Rua, número, bairro, cidade e UF.</small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Latitude</label>
          <input v-model="deliveryForm.baseLatitude" class="form-control" />
          <small>Opcional quando a base já foi localizada.</small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Longitude</label>
          <input v-model="deliveryForm.baseLongitude" class="form-control" />
          <small>Opcional quando a base já foi localizada.</small>
        </div>
        <div
          v-if="deliveryForm.useGoogleMaps && deliveryBaseMapUrl"
          class="modal-control-field modal-control-field-wide"
        >
          <label>Ponto base no mapa</label>
          <div
            v-if="googleMapsBrowserKey"
            ref="deliveryBaseMap"
            class="cardapio-map-preview"
          ></div>
          <iframe
            v-else
            class="cardapio-map-preview"
            :src="deliveryBaseMapUrl"
            title="Ponto base do restaurante"
            loading="lazy"
          ></iframe>
          <small>
            Confira se o endereco aponta para a saida real das entregas. O
            sistema usa este ponto para calcular distancia e faixa de cobranca.
          </small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Raio inicial (km)</label>
          <input
            v-model.number="deliveryForm.radiusMinKm"
            class="form-control"
            type="number"
            step="0.1"
          />
          <small>Use 0 para iniciar no restaurante.</small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Raio final (km)</label>
          <input
            v-model.number="deliveryForm.radiusMaxKm"
            class="form-control"
            type="number"
            step="0.1"
          />
          <small
            >Clientes acima desse raio usam outra faixa ou são
            bloqueados.</small
          >
        </div>
        <div
          v-if="deliveryForm.useGoogleMaps && deliveryRadiusMapUrl"
          class="modal-control-field modal-control-field-wide"
        >
          <label>Área de cobrança no mapa</label>
          <div
            v-if="googleMapsBrowserKey"
            ref="deliveryRadiusMap"
            class="cardapio-map-preview"
          ></div>
          <iframe
            v-else
            class="cardapio-map-preview"
            :src="deliveryRadiusMapUrl"
            title="Área de entrega"
            loading="lazy"
          ></iframe>
          <small>
            Use o raio para separar faixas de taxa. A validação final acontece
            no backend.
          </small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Bloquear esta faixa</label>
          <span class="switch switch-outline switch-icon switch-danger">
            <label>
              <input v-model="deliveryForm.blockedArea" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Use para bairros/raios sem atendimento.</small>
        </div>
        <div v-if="deliveryForm.useGoogleMaps" class="modal-control-field">
          <label>Frete grátis acima de</label>
          <money
            v-model="deliveryForm.freeAboveText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Deixe R$ 0,00 quando não houver frete grátis.</small>
        </div>
        <div class="modal-control-field">
          <label>Resumo</label>
          <input class="form-control" :value="deliveryPreviewLabel" readonly />
          <small>Conferencia dos valores exibidos ao cliente.</small>
        </div>
      </div>

      <div v-if="!deliveryForm.useGoogleMaps" class="delivery-zones">
        <div class="delivery-zones__head">
          <div>
            <strong>Zonas por bairro/CEP (opcional)</strong>
            <small>
              Defina taxa, pedido mínimo e tempo por bairro ou faixa de CEP.
              Endereços fora das zonas usam a taxa única acima.
            </small>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-light font-weight-bold"
            @click="newZone"
          >
            Adicionar zona
          </button>
        </div>

        <p v-if="!deliveryZones.length" class="delivery-zones__empty">
          Nenhuma zona cadastrada. A taxa única vale para toda a área atendida.
        </p>

        <ul v-else class="delivery-zones__list">
          <li
            v-for="zone in deliveryZones"
            :key="zone.id"
            class="delivery-zones__item"
          >
            <div class="delivery-zones__info">
              <strong>{{ zone.nome }}</strong>
              <small>{{ zoneScopeLabel(zone) }}</small>
              <small>
                Taxa {{ moneyTextFromCents(zone.taxa_entrega_centavos) }} · mín.
                {{ moneyTextFromCents(zone.pedido_minimo_centavos) }}
                <template v-if="zone.bloqueada"> · bloqueada</template>
                <template v-else-if="!zone.ativo"> · inativa</template>
              </small>
            </div>
            <div class="delivery-zones__actions">
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click="editZone(zone)"
              >
                Editar
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light"
                @click="toggleZone(zone)"
              >
                {{ zone.ativo ? "Desativar" : "Ativar" }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light text-danger"
                @click="deleteZone(zone)"
              >
                Excluir
              </button>
            </div>
          </li>
        </ul>

        <div
          v-if="zoneForm.open"
          class="delivery-zones__form modal-control-grid"
        >
          <div class="modal-control-field modal-control-field-wide">
            <strong>{{ zoneForm.id ? "Editar zona" : "Nova zona" }}</strong>
          </div>
          <div class="modal-control-field">
            <label>Nome da zona</label>
            <input v-model="zoneForm.nome" class="form-control" />
          </div>
          <div class="modal-control-field">
            <label>Bairro</label>
            <input v-model="zoneForm.bairro" class="form-control" />
            <small>Casa pelo nome do bairro informado pelo cliente.</small>
          </div>
          <div class="modal-control-field">
            <label>CEP inicial</label>
            <input v-model="zoneForm.cepInicio" class="form-control" />
          </div>
          <div class="modal-control-field">
            <label>CEP final</label>
            <input v-model="zoneForm.cepFim" class="form-control" />
            <small>Preencha bairro OU faixa de CEP.</small>
          </div>
          <div class="modal-control-field">
            <label>Taxa de entrega</label>
            <money
              v-model="zoneForm.feeText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
          </div>
          <div class="modal-control-field">
            <label>Pedido mínimo</label>
            <money
              v-model="zoneForm.minimumText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
          </div>
          <div class="modal-control-field">
            <label>Frete grátis acima de</label>
            <money
              v-model="zoneForm.freeAboveText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
            <small>Deixe R$ 0,00 quando não houver.</small>
          </div>
          <div class="modal-control-field">
            <label>Tempo mínimo</label>
            <input
              v-model.number="zoneForm.minMinutes"
              class="form-control"
              type="number"
            />
          </div>
          <div class="modal-control-field">
            <label>Tempo máximo</label>
            <input
              v-model.number="zoneForm.maxMinutes"
              class="form-control"
              type="number"
            />
          </div>
          <div class="modal-control-field">
            <label>Bloquear esta zona</label>
            <span class="switch switch-outline switch-icon switch-danger">
              <label>
                <input v-model="zoneForm.bloqueada" type="checkbox" />
                <span></span>
              </label>
            </span>
            <small>Recusa entregas para o bairro/CEP.</small>
          </div>
          <div
            class="modal-control-field modal-control-field-wide delivery-zones__form-actions"
          >
            <button
              type="button"
              class="btn btn-light font-weight-bold"
              @click="cancelZone"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-primary font-weight-bold"
              :disabled="isSaving('delivery-zone')"
              @click="saveZone"
            >
              {{ isSaving("delivery-zone") ? "Salvando..." : "Salvar zona" }}
            </button>
          </div>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioDelivery')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('delivery')"
          @click="saveDelivery"
        >
          {{ isSaving("delivery") ? "Salvando..." : "Salvar entrega" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioPickup" title="Retirada" size="lg" hide-footer>
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Permitir retirada no balcao</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="deliveryForm.pickupEnabled" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Retirada nao pede endereco do cliente.</small>
        </div>
        <div class="modal-control-field">
          <label>Tempo de preparo</label>
          <input
            v-model.number="deliveryForm.pickupMinutes"
            class="form-control"
            type="number"
          />
          <small>Estimativa mostrada para retirada.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Instrucao para o cliente</label>
          <textarea
            v-model="deliveryForm.pickupMessage"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Ex.: retire no balcao informando o codigo do pedido.</small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPickup')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('pickup')"
          @click="savePickup"
        >
          {{ isSaving("pickup") ? "Salvando..." : "Salvar retirada" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioDeliveryCost"
      title="Calculo de custo de entrega"
      size="lg"
      hide-footer
      @shown="renderDeliveryMaps"
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Tipo de calculo</label>
          <select v-model="deliveryForm.costMode" class="form-control">
            <option value="fixed">Taxa fixa</option>
            <option value="area">Area de entrega</option>
            <option value="free_minimum">Gratis acima de um valor</option>
          </select>
          <small>Escolha como a taxa sera explicada para a equipe.</small>
        </div>
        <div class="modal-control-field">
          <label>Area principal</label>
          <input v-model="deliveryForm.areaName" class="form-control" />
          <small>Bairro, regiao ou raio atendido.</small>
        </div>
        <div class="modal-control-field">
          <label>Raio inicial (km)</label>
          <input
            v-model.number="deliveryForm.radiusMinKm"
            class="form-control"
            type="number"
            step="0.1"
          />
          <small>Use 0 para iniciar no restaurante.</small>
        </div>
        <div class="modal-control-field">
          <label>Raio final (km)</label>
          <input
            v-model.number="deliveryForm.radiusMaxKm"
            class="form-control"
            type="number"
            step="0.1"
          />
          <small
            >Clientes acima desse raio usam outra faixa ou são
            bloqueados.</small
          >
        </div>
        <div
          v-if="deliveryRadiusMapUrl"
          class="modal-control-field modal-control-field-wide"
        >
          <label>Area de cobranca no mapa</label>
          <div
            v-if="googleMapsBrowserKey"
            ref="deliveryRadiusMap"
            class="cardapio-map-preview"
          ></div>
          <iframe
            v-else
            class="cardapio-map-preview"
            :src="deliveryRadiusMapUrl"
            title="Area de entrega"
            loading="lazy"
          ></iframe>
          <small>
            Use o raio para separar faixas de taxa. A exibicao abaixo ajuda a
            conferir a base; a validacao final acontece no backend.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Bloquear esta faixa</label>
          <span class="switch switch-outline switch-icon switch-danger">
            <label>
              <input v-model="deliveryForm.blockedArea" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Use para bairros/raios sem atendimento.</small>
        </div>
        <div class="modal-control-field">
          <label>Frete gratis acima de</label>
          <money
            v-model="deliveryForm.freeAboveText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>Deixe R$ 0,00 quando não houver frete grátis.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Observacao interna</label>
          <textarea
            v-model="deliveryForm.costNote"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Texto para orientar atendimento e conferencia.</small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioDeliveryCost')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('delivery-cost')"
          @click="saveDeliveryCost"
        >
          {{ isSaving("delivery-cost") ? "Salvando..." : "Salvar regra" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioProduct"
      title="Produto no site"
      size="xl"
      hide-footer
    >
      <div v-if="productForm">
        <div class="product-modal-header">
          <div class="product-photo-preview product-photo-preview-sm">
            <img
              v-if="productForm.imagePreviewUrl || productForm.imageUrl"
              :src="productForm.imagePreviewUrl || productForm.imageUrl"
              :alt="productForm.name"
            />
            <span v-else>{{ productForm.initials }}</span>
          </div>
          <div>
            <strong>{{ productForm.name }}</strong>
            <small>{{ categoryName(productForm.categoryId) }}</small>
          </div>
        </div>

        <div class="product-tabs">
          <button
            v-for="tab in productTabs"
            :key="tab.value"
            type="button"
            :class="{
              'product-tab-active': productForm.activeTab === tab.value,
            }"
            @click="productForm.activeTab = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="productForm.activeTab === 'data'" class="product-modal-grid">
          <div class="modal-control-field">
            <label>Nome no site</label>
            <input v-model="productForm.name" class="form-control" />
            <small>Nome curto e claro para o cliente.</small>
            <small v-if="fieldError('nome_publico')" class="text-danger">
              {{ fieldError("nome_publico") }}
            </small>
          </div>
          <div class="modal-control-field">
            <label>Categoria</label>
            <select v-model="productForm.categoryId" class="form-control">
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
            <small>Define onde aparece no cardápio.</small>
          </div>
          <div class="modal-control-field">
            <label>Preço exibido</label>
            <money
              v-model="productForm.priceText"
              class="form-control"
              v-bind="moneyOptions"
            ></money>
            <small>Ex.: R$ 29,90.</small>
            <small v-if="fieldError('preco_centavos')" class="text-danger">
              {{ fieldError("preco_centavos") }}
            </small>
          </div>
          <div class="modal-control-field">
            <label>Tag curta</label>
            <input v-model="productForm.tag" class="form-control" />
            <small>Ex.: Mais pedido, Combo, Novo.</small>
          </div>
          <div class="modal-control-field modal-control-field-wide">
            <label>Descrição</label>
            <textarea
              v-model="productForm.description"
              class="form-control"
              rows="3"
            ></textarea>
            <small>Texto que aparece no card e no detalhe.</small>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'photo'"
          class="product-media-panel"
        >
          <div class="product-photo-preview product-photo-preview-lg">
            <img
              v-if="productForm.imagePreviewUrl || productForm.imageUrl"
              :src="productForm.imagePreviewUrl || productForm.imageUrl"
              :alt="productForm.name"
            />
            <span v-else>{{ productForm.initials }}</span>
          </div>
          <div class="modal-control-field">
            <label>Foto do produto</label>
            <input
              class="form-control"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              @change="onProductImageSelected"
            />
            <small>
              Escolha uma foto do produto para aparecer no cardápio.
            </small>
            <small v-if="fieldError('imagem')" class="text-danger">
              {{ fieldError("imagem") }}
            </small>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'options'"
          class="product-config-panel"
        >
          <div class="guided-option-panel">
            <div>
              <strong>Adicionar escolhas deste produto</strong>
              <p>
                Escolha o tipo de personalização e o formulário já vem com
                exemplos prontos para editar.
              </p>
            </div>
            <div class="guided-option-grid">
              <button
                v-for="preset in optionGroupPresets"
                :key="`product-${preset.key}`"
                type="button"
                @click="openProductOptionGroupModal(productForm, preset.key)"
              >
                <strong>{{ preset.label }}</strong>
                <span>{{ preset.short }}</span>
              </button>
            </div>
          </div>

          <div class="toolbar-line">
            <div>
              <h5>Opções deste produto</h5>
              <p>
                Cadastre aqui borda, adicionais, remoções e acompanhamentos que
                pertencem somente a este produto.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductOptionGroupModal(productForm)"
            >
              Novo grupo
            </button>
          </div>
          <div
            v-if="!productOptionGroups(productForm).length"
            class="loading-box"
          >
            Nenhum grupo exclusivo deste produto.
          </div>
          <div v-else class="product-linked-list">
            <article
              v-for="group in productOptionGroups(productForm)"
              :key="group.id"
            >
              <div>
                <strong>{{ group.nome }}</strong>
                <small>
                  {{ optionTypeLabel(group.tipo) }} ·
                  {{ group.opcoes.length }} opções
                </small>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-light-primary font-weight-bold"
                @click="openOptionGroupModal(group)"
              >
                Editar
              </button>
            </article>
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'pizza'"
          class="product-config-panel"
        >
          <div class="toolbar-line">
            <div>
              <h5>Variante</h5>
              <p>
                Use quando este produto precisar de tamanho e sabores antes de
                ir para o carrinho.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductPizzaModal(productForm)"
            >
              {{
                productPizzaTemplate(productForm)
                  ? "Editar variante"
                  : "Ativar variante"
              }}
            </button>
          </div>
          <div
            v-if="productPizzaTemplate(productForm)"
            class="product-linked-list"
          >
            <article>
              <div>
                <strong>
                  {{ productPizzaTemplate(productForm).nome_template }}
                </strong>
                <small>
                  {{ productPizzaTemplate(productForm).tamanhos.length }}
                  tamanhos ·
                  {{ productPizzaTemplate(productForm).sabores.length }}
                  sabores
                </small>
              </div>
              <span
                class="label label-inline"
                :class="
                  productPizzaTemplate(productForm).ativo
                    ? 'label-light-success'
                    : 'label-light-danger'
                "
              >
                {{
                  productPizzaTemplate(productForm).ativo ? "Ativa" : "Pausada"
                }}
              </span>
            </article>
          </div>
          <div v-else class="loading-box">
            Este produto ainda usa o fluxo comum de compra.
          </div>
        </div>

        <div
          v-if="productForm.activeTab === 'promotion'"
          class="product-config-panel"
        >
          <div class="toolbar-line">
            <div>
              <h5>Promoção</h5>
              <p>
                Marque o produto como destaque agora. A vitrine comercial
                completa fica na seção Promoções.
              </p>
            </div>
            <button
              type="button"
              class="btn btn-light-primary font-weight-bold"
              @click="openProductPromotionModal(productForm)"
            >
              Abrir promoções
            </button>
          </div>
          <label class="highlight-toggle-card">
            <input v-model="productForm.highlighted" type="checkbox" />
            <span>
              <strong>Destacar no cardápio</strong>
              <small>Aparece em destaques e sugestões do site.</small>
            </span>
          </label>
        </div>

        <div
          v-if="productForm.activeTab === 'availability'"
          class="product-config-panel"
        >
          <div class="modal-control-grid">
            <div class="modal-control-field">
              <label>Disponível no site</label>
              <span class="switch switch-outline switch-icon switch-success">
                <label>
                  <input v-model="productForm.available" type="checkbox" />
                  <span></span>
                </label>
              </span>
              <small>
                Use para pausar temporariamente sem apagar o produto.
              </small>
            </div>
            <div class="modal-control-field">
              <label>Preparo</label>
              <input
                class="form-control"
                :value="productForm.noPreparation ? 'Sem preparo' : 'Cozinha'"
                disabled
              />
              <small>O setor vem do cadastro operacional do produto.</small>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioProduct')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-light-dark font-weight-bold"
          @click="goToProducts"
        >
          Abrir cadastro oficial
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('product')"
          @click="saveProduct"
        >
          {{ isSaving("product") ? "Salvando..." : "Salvar produto" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioPromotions"
      title="Promoção comercial"
      size="xl"
      hide-footer
    >
      <div class="promotion-preset-grid mb-6">
        <button
          v-for="preset in promotionPresetOptions"
          :key="preset.value"
          type="button"
          :class="{
            'promotion-preset-active': promotionForm.preset === preset.value,
          }"
          @click="applyPromotionPreset(preset.value)"
        >
          <strong>{{ preset.label }}</strong>
          <span>{{ preset.description }}</span>
        </button>
      </div>

      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Título da promoção</label>
          <input v-model="promotionForm.name" class="form-control" />
          <small>Ex.: Terça da pizza, Combo do almoço, Semana do burger.</small>
          <small v-if="fieldError('nome')" class="text-danger">
            {{ fieldError("nome") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Subtítulo</label>
          <input v-model="promotionForm.subtitle" class="form-control" />
          <small>Texto curto que aparece abaixo do título da campanha.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição comercial</label>
          <textarea
            v-model="promotionForm.description"
            class="form-control"
            rows="2"
          ></textarea>
        </div>
        <div class="modal-control-field">
          <label>Onde aparece</label>
          <select v-model="promotionForm.targetType" class="form-control">
            <option value="produto">Produto específico</option>
            <option value="categoria">Categoria inteira</option>
            <option value="cardapio">Todo o cardápio</option>
          </select>
        </div>
        <div
          v-if="promotionForm.targetType === 'produto'"
          class="modal-control-field"
        >
          <label>Produto principal</label>
          <select v-model="promotionForm.productId" class="form-control">
            <option
              v-for="product in publishedProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Item principal da campanha.</small>
        </div>
        <div
          v-if="promotionForm.targetType === 'categoria'"
          class="modal-control-field"
        >
          <label>Categoria</label>
          <select v-model="promotionForm.categoryId" class="form-control">
            <option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Imagem da vitrine</label>
          <div class="promotion-image-row">
            <div class="promotion-image-preview">
              <img
                v-if="promotionImagePreview"
                :src="promotionImagePreview"
                alt="Prévia da promoção"
              />
              <span v-else>{{ promotionForm.name || "Promoção" }}</span>
            </div>
            <div>
              <input
                class="form-control"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                @change="onPromotionImageSelected"
              />
              <small>
                Use uma imagem horizontal. Ela aparece na vitrine de promoções
                do site.
              </small>
            </div>
          </div>
        </div>
        <div class="modal-control-field">
          <label>Tipo de desconto</label>
          <select v-model="promotionForm.discountType" class="form-control">
            <option value="valor">Valor fixo</option>
            <option value="percentual">Percentual</option>
          </select>
        </div>
        <div class="modal-control-field">
          <label>
            {{
              promotionForm.discountType === "percentual"
                ? "Percentual de desconto"
                : "Valor de desconto"
            }}
          </label>
          <input
            v-if="promotionForm.discountType === 'percentual'"
            v-model.number="promotionForm.discountPercent"
            class="form-control"
            min="0"
            max="100"
            type="number"
          />
          <money
            v-else
            v-model="promotionForm.discountText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>O backend recalcula o preço final no checkout.</small>
        </div>
        <div class="modal-control-field">
          <label>Preço anterior exibido</label>
          <money
            v-model="promotionForm.previousPriceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Preço promocional exibido</label>
          <money
            v-model="promotionForm.promotionalPriceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <small>
            Quando informado para um produto, calculamos o desconto a partir do
            preço atual.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Início</label>
          <input
            v-model="promotionForm.start"
            class="form-control"
            type="date"
          />
        </div>
        <div class="modal-control-field">
          <label>Fim</label>
          <input v-model="promotionForm.end" class="form-control" type="date" />
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Chamada curta do banner</label>
          <input v-model="promotionForm.bannerText" class="form-control" />
          <small>Chamada curta para aparecer na vitrine do cardápio.</small>
        </div>
        <div
          v-if="promotionForm.preset === 'compre_ganhe'"
          class="modal-control-field modal-control-field-wide"
        >
          <label>O que o cliente ganha</label>
          <input v-model="promotionForm.giftText" class="form-control" />
          <small>Ex.: Ganhe uma bebida, sobremesa ou borda grátis.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Dias da semana</label>
          <div class="weekday-picker">
            <label v-for="day in weekdays" :key="day.value">
              <input
                v-model="promotionForm.weekdays"
                type="checkbox"
                :value="day.value"
              />
              <span>{{ day.label }}</span>
            </label>
          </div>
          <small>Sem seleção significa disponível em todos os dias.</small>
        </div>
        <div class="modal-control-field">
          <label>Promoção ativa</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="promotionForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPromotions')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('promotion')"
          @click="savePromotion"
        >
          {{ isSaving("promotion") ? "Salvando..." : "Salvar promoção" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioCoupon" title="Cupom" size="lg" hide-footer>
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Código</label>
          <input v-model="couponForm.code" class="form-control" />
          <small>Ex.: PIZZA10, RETIRA5, CLIENTE20.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome interno</label>
          <input v-model="couponForm.name" class="form-control" />
        </div>
        <div class="modal-control-field">
          <label>Tipo de desconto</label>
          <select v-model="couponForm.discountType" class="form-control">
            <option value="valor">Valor fixo</option>
            <option value="percentual">Percentual</option>
          </select>
        </div>
        <div class="modal-control-field">
          <label>Desconto</label>
          <input
            v-if="couponForm.discountType === 'percentual'"
            v-model.number="couponForm.discountPercent"
            class="form-control"
            min="0"
            max="100"
            type="number"
          />
          <money
            v-else
            v-model="couponForm.discountText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Pedido mínimo</label>
          <money
            v-model="couponForm.minimumText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Limite total de uso</label>
          <input
            v-model.number="couponForm.totalLimit"
            class="form-control"
            min="1"
            type="number"
          />
        </div>
        <div class="modal-control-field">
          <label>Início</label>
          <input v-model="couponForm.start" class="form-control" type="date" />
        </div>
        <div class="modal-control-field">
          <label>Fim</label>
          <input v-model="couponForm.end" class="form-control" type="date" />
        </div>
        <div class="modal-control-field">
          <label>Cupom ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="couponForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCoupon')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('coupon')"
          @click="saveCoupon"
        >
          {{ isSaving("coupon") ? "Salvando..." : "Salvar cupom" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioCombo" title="Combo" size="lg" hide-footer>
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Produto publicado</label>
          <select v-model="comboForm.productId" class="form-control">
            <option
              v-for="product in publishedProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Este produto passa a representar o combo no cardápio.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome do combo</label>
          <input v-model="comboForm.name" class="form-control" />
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Descrição</label>
          <textarea
            v-model="comboForm.description"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
        <div class="modal-control-field">
          <label>Preço do combo</label>
          <money
            v-model="comboForm.priceText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
        </div>
        <div class="modal-control-field">
          <label>Combo ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="comboForm.enabled" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Composição</label>
          <textarea
            v-model="comboForm.itemsText"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Informe um item por linha. Ex.: Burger, batata, bebida.</small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCombo')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('combo')"
          @click="saveCombo"
        >
          {{ isSaving("combo") ? "Salvando..." : "Salvar combo" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioCheckout"
      title="Pagamento presencial"
      size="lg"
      hide-footer
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Pix presencial</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="checkoutForm.pix" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small
            >Cliente paga ao motoboy ou no balcão. A equipe registra a
            baixa.</small
          >
        </div>
        <div class="modal-control-field">
          <label>Dinheiro presencial</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="checkoutForm.cash" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Permite informar troco para entrega ou retirada.</small>
        </div>
        <div class="modal-control-field">
          <label>Cartão presencial</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="checkoutForm.card" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Recebido na maquininha do balcão ou do entregador.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Mensagem ao cliente</label>
          <textarea
            v-model="checkoutForm.message"
            class="form-control"
            rows="3"
          ></textarea>
          <small>Texto exibido antes de finalizar.</small>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioCheckout')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('checkout')"
          @click="saveCheckout"
        >
          {{
            isSaving("checkout") ? "Salvando..." : "Salvar pagamento presencial"
          }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioOperation"
      title="Operacao dos pedidos"
      size="lg"
      hide-footer
    >
      <div class="modal-control-grid">
        <div class="modal-control-field">
          <label>Aceite manual</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="operationForm.manualAccept" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Equipe decide aceitar ou recusar cada pedido.</small>
        </div>
        <div class="modal-control-field">
          <label>Imprimir pedido</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="operationForm.printOrder" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Usa a impressao interna do restaurante.</small>
        </div>
        <div class="modal-control-field">
          <label>Alerta de novo pedido</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="operationForm.sound" type="checkbox" />
              <span></span>
            </label>
          </span>
          <small>Ajuda a equipe a perceber novas entradas.</small>
        </div>
        <div class="modal-control-field modal-control-field-wide">
          <label>Instrucao para equipe</label>
          <textarea
            v-model="operationForm.note"
            class="form-control"
            rows="3"
          ></textarea>
        </div>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioOperation')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('operation')"
          @click="saveOperation"
        >
          {{ isSaving("operation") ? "Salvando..." : "Salvar operacao" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioVisual"
      title="Visual e textos"
      size="lg"
      hide-footer
      @shown="initVisualPreview"
    >
      <div class="visual-editor">
        <div>
          <b-tabs pills content-class="pt-5">
            <b-tab title="Marca" active>
              <div class="visual-upload-card">
                <label>Logo do cardápio</label>
                <div class="visual-logo-preview">
                  <img
                    v-if="visualLogoPreview"
                    :src="visualLogoPreview"
                    alt="Prévia da logo"
                  />
                  <span v-else>{{ config.name ? config.name[0] : "C" }}</span>
                </div>
                <input
                  class="form-control"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  @change="onVisualImageSelected($event, 'logo')"
                />
                <small>
                  Use uma imagem quadrada. Ela aparece no topo, busca e
                  checkout.
                </small>
                <small v-if="fieldError('logo')" class="text-danger">
                  {{ fieldError("logo") }}
                </small>
              </div>
            </b-tab>

            <b-tab title="Capa">
              <div class="visual-upload-card">
                <label>Capa do site</label>
                <div class="visual-cover-preview">
                  <img
                    v-if="visualCoverPreview"
                    :src="visualCoverPreview"
                    alt="Prévia da capa"
                  />
                  <div v-else>
                    <strong>{{ config.name || "Cardápio Online" }}</strong>
                    <span>Imagem principal exibida no topo do site.</span>
                  </div>
                </div>
                <input
                  class="form-control"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  @change="onVisualImageSelected($event, 'capa')"
                />
                <small>
                  Foto horizontal de ambiente, produto ou fachada. Quando não
                  houver foto, o site usa a capa padrão ao lado.
                </small>
                <small v-if="fieldError('capa')" class="text-danger">
                  {{ fieldError("capa") }}
                </small>
              </div>
            </b-tab>

            <b-tab title="Cores">
              <div class="modal-control-grid">
                <div class="modal-control-field">
                  <label>Cor principal</label>
                  <input
                    v-model="visualForm.primaryColor"
                    class="form-control"
                  />
                  <small>Botões principais, abas ativas e destaques.</small>
                </div>
                <div class="modal-control-field">
                  <label>Cor de apoio</label>
                  <input
                    v-model="visualForm.accentColor"
                    class="form-control"
                  />
                  <small>Banners, detalhes suaves e áreas promocionais.</small>
                </div>
                <div class="modal-control-field">
                  <label>Cor de fundo</label>
                  <input
                    v-model="visualForm.backgroundColor"
                    class="form-control"
                  />
                  <small>Fundo principal do cardápio público.</small>
                </div>
              </div>
              <div v-if="visualContrastWarning" class="visual-warning">
                {{ visualContrastWarning }}
              </div>
            </b-tab>

            <b-tab title="Estilo">
              <p class="visual-choice-label">Estilo dos cards</p>
              <div class="visual-choice-grid">
                <button
                  v-for="style in cardStyleOptions"
                  :key="style.value"
                  type="button"
                  class="visual-choice"
                  :class="{
                    'visual-choice--active':
                      visualForm.cardStyle === style.value,
                  }"
                  @click="visualForm.cardStyle = style.value"
                >
                  <strong>{{ style.label }}</strong>
                  <span>{{ style.description }}</span>
                </button>
              </div>

              <p class="visual-choice-label mt-4">Formato dos botões</p>
              <div class="visual-choice-grid">
                <button
                  v-for="style in buttonStyleOptions"
                  :key="style.value"
                  type="button"
                  class="visual-choice"
                  :class="{
                    'visual-choice--active':
                      visualForm.buttonStyle === style.value,
                  }"
                  @click="visualForm.buttonStyle = style.value"
                >
                  <strong>{{ style.label }}</strong>
                  <span>{{ style.description }}</span>
                </button>
              </div>
            </b-tab>

            <b-tab title="Textos">
              <div class="modal-control-grid">
                <div class="modal-control-field">
                  <label>Sem produtos</label>
                  <input v-model="visualForm.emptyText" class="form-control" />
                  <small>Aparece quando não houver itens disponíveis.</small>
                </div>
                <div class="modal-control-field">
                  <label>Erro ao carregar</label>
                  <input v-model="visualForm.errorText" class="form-control" />
                  <small>Aparece se o cardápio não abrir.</small>
                </div>
                <div class="modal-control-field">
                  <label>Checkout</label>
                  <input
                    v-model="visualForm.checkoutText"
                    class="form-control"
                  />
                  <small>Aparece antes de o cliente finalizar o pedido.</small>
                </div>
              </div>
            </b-tab>

            <b-tab title="Avisos">
              <div class="modal-control-grid">
                <div class="modal-control-field">
                  <label>Loja fechada</label>
                  <input v-model="visualForm.closedText" class="form-control" />
                  <small>
                    Aviso exibido quando a loja estiver pausada ou fechada.
                  </small>
                </div>
                <div class="modal-control-field">
                  <label>Acompanhamento</label>
                  <input
                    v-model="visualForm.trackingText"
                    class="form-control"
                  />
                  <small>Texto de apoio na tela de status do pedido.</small>
                </div>
              </div>
            </b-tab>

            <b-tab title="Preview">
              <div class="visual-preview-note">
                A prévia ao lado é o próprio cardápio público, atualizado em
                tempo real com as cores e estilos enquanto você edita. Os textos
                e imagens aparecem após salvar.
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <aside class="visual-live-preview">
          <iframe
            v-if="visualPreviewSrc"
            ref="visualPreviewFrame"
            :src="visualPreviewSrc"
            class="visual-live-preview__frame"
            title="Prévia do cardápio"
            loading="lazy"
            @load="pushVisualPreviewTheme"
          ></iframe>
          <div v-else class="visual-live-preview__empty">
            <strong>Prévia indisponível</strong>
            <span>
              Publique o cardápio (aba Publicação) para ver a prévia em tempo
              real aqui.
            </span>
          </div>
        </aside>
      </div>
      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioVisual')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('visual')"
          @click="saveVisual"
        >
          {{ isSaving("visual") ? "Salvando..." : "Salvar visual" }}
        </button>
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioOptionProductPicker"
      title="Criar opção em produto"
      size="lg"
      hide-footer
    >
      <div class="advanced-section-note">
        <strong>Escolha o produto</strong>
        <span>
          Use este caminho para borda, adicionais, molhos, acompanhamentos ou
          remoções que só fazem sentido em um item específico.
        </span>
      </div>

      <div class="option-product-picker">
        <button
          v-for="product in publishedProducts"
          :key="product.id"
          type="button"
          @click="selectProductForOptionGroup(product)"
        >
          <img v-if="product.imageUrl" :src="product.imageUrl" alt="" />
          <span v-else>{{ product.initials }}</span>
          <strong>{{ product.name }}</strong>
          <small>{{ categoryName(product.categoryId) }}</small>
        </button>
      </div>

      <div v-if="!publishedProducts.length" class="loading-box">
        Publique um produto no site antes de criar opções específicas.
      </div>
    </b-modal>

    <b-modal
      id="modalCardapioOptionGroup"
      :title="optionGroupModalTitle"
      size="xl"
      hide-footer
    >
      <div class="advanced-section-note">
        <strong>{{ optionGroupModalSubtitle }}</strong>
        <span>
          {{ optionGroupModalDescription }}
        </span>
      </div>

      <div v-if="optionGroupForm" class="option-preset-panel">
        <button
          v-for="preset in optionGroupPresets"
          :key="preset.key"
          type="button"
          :class="{
            'option-preset-active': optionGroupPresetMatches(preset),
          }"
          @click="applyOptionGroupPreset(preset.key)"
        >
          <strong>{{ preset.label }}</strong>
          <span>{{ preset.description }}</span>
        </button>
      </div>

      <div v-if="optionGroupForm" class="modal-control-grid">
        <div
          v-if="optionGroupForm.mode === 'produto'"
          class="modal-control-field"
        >
          <label>Produto</label>
          <input
            :value="productName(optionGroupForm.cardapio_produto_id)"
            class="form-control"
            disabled
          />
          <small>
            Este grupo aparece somente neste produto. Para outro item, abra o
            produto correspondente no catálogo.
          </small>
        </div>
        <div v-else class="modal-control-field">
          <label>Alcance global</label>
          <select v-model="optionGroupForm.globalScope" class="form-control">
            <option value="todos">Todo o cardápio</option>
            <option value="categorias">Categorias selecionadas</option>
          </select>
          <small>Grupo global não pertence a um produto específico.</small>
        </div>
        <div
          v-if="
            optionGroupForm.mode === 'global' &&
            optionGroupForm.globalScope === 'categorias'
          "
          class="modal-control-field modal-control-field-wide"
        >
          <label>Categorias que recebem este grupo</label>
          <div class="option-category-picker">
            <label
              v-for="category in categories"
              :key="category.id"
              class="checkbox-line"
            >
              <input
                v-model="optionGroupForm.categoria_ids"
                type="checkbox"
                :value="category.id"
              />
              <span>{{ category.name }}</span>
            </label>
          </div>
          <small>
            Deixe como todo o cardápio quando a escolha deve aparecer em todos
            os produtos.
          </small>
        </div>
        <div class="modal-control-field">
          <label>Nome do grupo</label>
          <input v-model="optionGroupForm.nome" class="form-control" />
          <small v-if="fieldError('nome')" class="text-danger">
            {{ fieldError("nome") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Tipo</label>
          <select v-model="optionGroupForm.tipo" class="form-control">
            <option
              v-for="type in optionGroupTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
          <small>{{ optionGroupTypeHelp }}</small>
        </div>
        <div class="modal-control-field">
          <label>Mínimo</label>
          <input
            v-model.number="optionGroupForm.minimo"
            type="number"
            min="0"
            class="form-control"
          />
        </div>
        <div class="modal-control-field">
          <label>Máximo</label>
          <input
            v-model.number="optionGroupForm.maximo"
            type="number"
            min="1"
            class="form-control"
          />
        </div>
        <div class="modal-control-field">
          <label>Obrigatório</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="optionGroupForm.obrigatorio" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field">
          <label>Múltipla quantidade</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input
                v-model="optionGroupForm.permite_quantidade"
                type="checkbox"
              />
              <span></span>
            </label>
          </span>
        </div>
        <div class="modal-control-field">
          <label>Ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="optionGroupForm.ativo" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>

      <div v-if="optionGroupForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Itens do grupo</h5>
            <p>
              {{ optionGroupItemHint }}
            </p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addOptionItem"
          >
            Adicionar opção
          </button>
        </div>

        <div
          v-for="(option, index) in optionGroupForm.opcoes"
          :key="option.localKey"
          class="option-editor-row"
        >
          <input
            v-model="option.nome"
            class="form-control"
            placeholder="Nome da opção"
          />
          <input
            v-model="option.descricao"
            class="form-control"
            placeholder="Descrição curta"
          />
          <money
            v-model="option.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <details class="option-advanced-link">
            <summary>Vínculo interno</summary>
            <select v-model="option.produto_id" class="form-control">
              <option :value="null">Sem vínculo interno</option>
              <option
                v-for="product in products"
                :key="`interno-${product.id}`"
                :value="product.productId"
              >
                {{ product.name }}
              </option>
            </select>
          </details>
          <label class="checkbox-line">
            <input v-model="option.disponivel" type="checkbox" />
            Disponível
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removeOptionItem(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioOptionGroup')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('option-group')"
          @click="saveOptionGroup"
        >
          {{ isSaving("option-group") ? "Salvando..." : "Salvar grupo" }}
        </button>
      </div>
    </b-modal>

    <b-modal id="modalCardapioPizza" title="Variante" size="xl" hide-footer>
      <div v-if="pizzaForm" class="modal-control-grid">
        <div class="modal-control-field">
          <label>Produto publicado</label>
          <select v-model="pizzaForm.cardapio_produto_id" class="form-control">
            <option
              v-for="product in publishedProducts"
              :key="product.id"
              :value="product.id"
            >
              {{ product.name }}
            </option>
          </select>
          <small>Este produto passa a pedir tamanho e sabores no site.</small>
        </div>
        <div class="modal-control-field">
          <label>Nome da variante</label>
          <input v-model="pizzaForm.nome_template" class="form-control" />
          <small v-if="fieldError('nome_template')" class="text-danger">
            {{ fieldError("nome_template") }}
          </small>
        </div>
        <div class="modal-control-field">
          <label>Máximo padrão de sabores</label>
          <input
            v-model.number="pizzaForm.max_sabores_padrao"
            class="form-control"
            min="1"
            type="number"
          />
        </div>
        <div class="modal-control-field">
          <label>Template ativo</label>
          <span class="switch switch-outline switch-icon switch-success">
            <label>
              <input v-model="pizzaForm.ativo" type="checkbox" />
              <span></span>
            </label>
          </span>
        </div>
      </div>

      <div v-if="pizzaForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Tamanhos</h5>
            <p>
              Defina preço base e limite de sabores. O valor final soma o preço
              do tamanho com os adicionais dos sabores escolhidos.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addPizzaSize"
          >
            Adicionar tamanho
          </button>
        </div>

        <div
          v-for="(size, index) in pizzaForm.tamanhos"
          :key="size.localKey"
          class="option-editor-row"
        >
          <input v-model="size.nome" class="form-control" placeholder="Nome" />
          <money
            v-model="size.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <input
            v-model.number="size.max_sabores"
            class="form-control"
            min="1"
            type="number"
          />
          <input
            v-model.number="size.fatias"
            class="form-control"
            min="1"
            type="number"
            placeholder="Fatias"
          />
          <label class="checkbox-line">
            <input v-model="size.ativo" type="checkbox" />
            Ativo
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removePizzaSize(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div v-if="pizzaForm" class="option-editor mt-6">
        <div class="toolbar-line">
          <div>
            <h5>Sabores</h5>
            <p>
              Informe nome e preço de referência. Restrinja por tamanho somente
              quando o sabor não serve para todos.
            </p>
          </div>
          <button
            type="button"
            class="btn btn-light-primary font-weight-bold"
            @click="addPizzaFlavor"
          >
            Adicionar sabor
          </button>
        </div>

        <div
          v-for="(flavor, index) in pizzaForm.sabores"
          :key="flavor.localKey"
          class="option-editor-row option-editor-row-wide"
        >
          <input
            v-model="flavor.nome"
            class="form-control"
            placeholder="Nome"
          />
          <input
            v-model="flavor.descricao"
            class="form-control"
            placeholder="Descrição"
          />
          <money
            v-model="flavor.precoText"
            class="form-control"
            v-bind="moneyOptions"
          ></money>
          <select v-model="flavor.tamanhos" class="form-control" multiple>
            <option
              v-for="size in pizzaForm.tamanhos"
              :key="size.localKey"
              :value="size.slug"
            >
              {{ size.nome || size.slug }}
            </option>
          </select>
          <label class="checkbox-line">
            <input v-model="flavor.disponivel" type="checkbox" />
            Disponível
          </label>
          <button
            type="button"
            class="btn btn-sm btn-light-danger font-weight-bold"
            @click="removePizzaFlavor(index)"
          >
            Remover
          </button>
        </div>
      </div>

      <div class="modal-footer px-0 pb-0">
        <button
          type="button"
          class="btn btn-light font-weight-bold"
          @click="$bvModal.hide('modalCardapioPizza')"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary font-weight-bold"
          :disabled="isSaving('pizza')"
          @click="savePizza"
        >
          {{ isSaving("pizza") ? "Salvando..." : "Salvar variante" }}
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Money } from "v-money";

const SECTION_CARDS = {
  promotions: [
    {
      title: "Destaques do cardapio",
      status: "Pronto",
      labelClass: "label-light-success",
      description: "Escolha produtos para aparecerem com mais forca no site.",
      items: ["Mais pedido", "Combo", "Novidade"],
      buttonLabel: "Configurar destaques",
      action: "promotions",
    },
    {
      title: "Banners curtos",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Mensagem simples, sem ocupar a primeira tela inteira.",
      items: ["Chamada curta", "Periodo", "Produto relacionado"],
      buttonLabel: "Editar banner",
      action: "promotions",
    },
    {
      title: "Cupons",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Configure codigos de desconto e validade.",
      items: ["Codigo", "Validade", "Pedido minimo"],
      buttonLabel: "Configurar cupons",
      action: "promotions",
    },
  ],
  delivery: [
    {
      title: "Entrega",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Taxa, pedido minimo e tempo que aparecem no checkout.",
      items: ["Taxa fixa", "Pedido minimo", "Google API opcional"],
      buttonLabel: "Configurar entrega",
      action: "delivery",
    },
  ],
  pickup: [
    {
      title: "Retirada",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Permite retirar no balcao sem pedir endereco.",
      items: ["Retirada no balcao", "Tempo de preparo", "Instrucao ao cliente"],
      buttonLabel: "Configurar retirada",
      action: "pickup",
    },
  ],
  checkout: [
    {
      title: "Formas de pagamento",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Pix, dinheiro e cartão recebidos na entrega ou no balcão.",
      items: ["Baixa manual", "Troco", "Motoboy ou balcão"],
      buttonLabel: "Configurar pagamento",
      action: "checkout",
    },
    {
      title: "Dados do cliente",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Campos essenciais para concluir o pedido.",
      items: ["Nome", "Telefone", "Endereco para entrega"],
      buttonLabel: "Editar mensagem",
      action: "checkout",
    },
    {
      title: "Avisos de validacao",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Mensagens claras quando algo impede o pedido.",
      items: ["Pedido minimo", "Produto pausado", "Loja fechada"],
      buttonLabel: "Ajustar textos",
      action: "checkout",
    },
  ],
  operation: [
    {
      title: "Pedidos online",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Entrada dos pedidos para a equipe aceitar e acompanhar.",
      items: ["Aguardando aceite", "Em preparo", "Saiu para entrega"],
      buttonLabel: "Configurar operacao",
      action: "operation",
    },
    {
      title: "Impressao",
      status: "Interna",
      labelClass: "label-light-primary",
      description: "Impressao do pedido para atendimento e cozinha.",
      items: ["Via do pedido", "Via de preparo", "Codigo publico"],
      buttonLabel: "Ajustar impressao",
      action: "operation",
    },
    {
      title: "Historico",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Rastro das mudancas importantes de cada pedido.",
      items: ["Aceite", "Mudanca de status", "Pagamento"],
      buttonLabel: "Ver fluxo",
      action: "operation",
    },
  ],
  visual: [
    {
      title: "Logo e cores",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Identidade visual usada como referencia do site.",
      items: ["Logo Nuts", "Cor principal", "Contraste"],
      buttonLabel: "Editar visual",
      action: "visual",
    },
    {
      title: "Textos publicos",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Mensagens para vazio, erro, loja fechada e pagamento.",
      items: ["Sem produtos", "Erro ao carregar", "Pix aguardando"],
      buttonLabel: "Editar textos",
      action: "visual",
    },
    {
      title: "Mobile",
      status: "Revisado",
      labelClass: "label-light-primary",
      description: "Experiencia curta para cliente usando celular.",
      items: ["Busca", "Categorias", "Carrinho flutuante"],
      buttonLabel: "Ver site",
      action: "preview",
    },
  ],
  products: [
    {
      title: "Fotos",
      status: "Local",
      labelClass: "label-light-success",
      description: "Imagens locais para deixar o cardapio apresentavel.",
      items: ["Card", "Detalhe", "Fallback sem foto"],
    },
    {
      title: "Adicionais",
      status: "Ativo",
      labelClass: "label-light-success",
      description: "Espaco para escolhas e complementos por produto.",
      items: ["Obrigatorio", "Opcional", "Limite por grupo"],
    },
    {
      title: "Preparo",
      status: "Ativo",
      labelClass: "label-light-success",
      description:
        "Produtos com preparo seguem para cozinha; bebidas podem sair sem preparo.",
      items: ["Cozinha", "Bar", "Sem preparo"],
    },
  ],
};

export default {
  name: "app-restaurante-cardapio-online",
  components: {
    Money,
  },
  data() {
    return {
      activeSection: "overview",
      managementLoading: false,
      saving: {},
      fieldErrors: {},
      moneyOptions: {
        decimal: ",",
        thousands: ".",
        prefix: "R$ ",
        suffix: "",
        precision: 2,
        masked: true,
      },
      productForm: null,
      optionGroupForm: null,
      pizzaForm: null,
      productTabs: [
        { value: "data", label: "Dados do site" },
        { value: "photo", label: "Foto" },
        { value: "options", label: "Opções" },
        { value: "pizza", label: "Variante" },
        { value: "promotion", label: "Promoção" },
        { value: "availability", label: "Disponibilidade" },
      ],
      weekdays: [
        { value: 0, label: "Dom" },
        { value: 1, label: "Seg" },
        { value: 2, label: "Ter" },
        { value: 3, label: "Qua" },
        { value: 4, label: "Qui" },
        { value: 5, label: "Sex" },
        { value: 6, label: "Sáb" },
      ],
      pauseOptions: [
        {
          value: "manual",
          label: "Até ativar novamente",
          description: "Mantém indisponível até alguém reativar.",
        },
        {
          value: "hour",
          label: "Por 1 hora",
          description: "Bom para pausa rápida de preparo ou estoque.",
        },
        {
          value: "today",
          label: "Até o fim do dia",
          description: "Volta automaticamente amanhã.",
        },
        {
          value: "custom",
          label: "Escolher data e hora",
          description: "Use quando já souber quando a venda retorna.",
        },
      ],
      pauseForm: {
        type: null,
        target: null,
        typeLabel: "",
        name: "",
        mode: "manual",
        customUntil: "",
        reason: "",
        message: "",
        savingKey: "pause",
      },
      optionGroupTypes: [
        { value: "escolha_unica", label: "Escolha única" },
        { value: "multipla_escolha", label: "Múltipla escolha" },
        { value: "adicional_pago", label: "Adicional pago" },
        { value: "remocao_sem_preco", label: "Remoção sem preço" },
        { value: "observacao_textual", label: "Observação textual" },
        {
          value: "acompanhamento_obrigatorio",
          label: "Acompanhamento obrigatório",
        },
      ],
      optionGroupPresets: [
        {
          key: "borda",
          label: "Borda",
          short: "Sem borda, recheada",
          description: "Para pizza, lanche ou prato com opção de borda.",
          name: "Borda",
          type: "escolha_unica",
          min: 0,
          max: 1,
          required: false,
          quantity: false,
          items: [
            {
              name: "Sem borda",
              description: "Produto sem borda adicional",
              price: 0,
            },
            {
              name: "Borda recheada",
              description: "Borda com recheio",
              price: 800,
            },
          ],
        },
        {
          key: "massa",
          label: "Massa",
          short: "Tradicional, fina",
          description: "Para escolher uma base ou preparo principal.",
          name: "Massa",
          type: "escolha_unica",
          min: 1,
          max: 1,
          required: true,
          quantity: false,
          items: [
            {
              name: "Massa tradicional",
              description: "Padrão da casa",
              price: 0,
            },
            {
              name: "Massa fina",
              description: "Mais leve e crocante",
              price: 0,
            },
          ],
        },
        {
          key: "adicional",
          label: "Adicional",
          short: "Bacon, cheddar",
          description: "Para itens pagos que o cliente pode incluir.",
          name: "Adicionais",
          type: "adicional_pago",
          min: 0,
          max: null,
          required: false,
          quantity: true,
          items: [
            { name: "Cheddar", description: "Porção extra", price: 500 },
            { name: "Bacon", description: "Porção extra", price: 700 },
          ],
        },
        {
          key: "acompanhamento",
          label: "Acompanhamento",
          short: "Batata, salada",
          description: "Para escolher complemento do item.",
          name: "Acompanhamento",
          type: "multipla_escolha",
          min: 0,
          max: 2,
          required: false,
          quantity: false,
          items: [
            { name: "Batata frita", description: "Porção pequena", price: 900 },
            {
              name: "Salada simples",
              description: "Acompanha o pedido",
              price: 0,
            },
          ],
        },
        {
          key: "molho",
          label: "Molho",
          short: "Barbecue, maionese",
          description: "Para molhos extras ou escolhas de molho.",
          name: "Molhos",
          type: "multipla_escolha",
          min: 0,
          max: 3,
          required: false,
          quantity: true,
          items: [
            {
              name: "Molho barbecue",
              description: "Sachê ou pote",
              price: 250,
            },
            {
              name: "Maionese da casa",
              description: "Pote pequeno",
              price: 300,
            },
          ],
        },
        {
          key: "remocao",
          label: "Remover ingrediente",
          short: "Sem cebola, sem molho",
          description: "Para o cliente retirar algo sem alterar preço.",
          name: "Remover ingredientes",
          type: "remocao_sem_preco",
          min: 0,
          max: null,
          required: false,
          quantity: false,
          items: [
            { name: "Sem cebola", description: "Retirar cebola", price: 0 },
            { name: "Sem molho", description: "Retirar molho", price: 0 },
          ],
        },
        {
          key: "observacao",
          label: "Observação",
          short: "Campo de texto",
          description: "Para o cliente escrever um pedido específico.",
          name: "Observação do item",
          type: "observacao_textual",
          min: 0,
          max: 1,
          required: false,
          quantity: false,
          items: [],
        },
      ],
      config: {
        slug: "restaurante-demo",
        publicUrl: "http://localhost:5174/restaurante-demo",
        name: "Nuts Burger",
        description: "Hamburgueria artesanal com entrega e retirada.",
        habilitado: true,
        deliveryEnabled: true,
        pickupEnabled: true,
        scheduleLabel: "Hoje, 18:00 as 23:30",
        closedMessage: "Estamos fechados agora. Consulte o proximo horario.",
        minimumOrderCents: 2500,
        deliveryFeeCents: 700,
        minMinutes: 35,
        maxMinutes: 50,
        baseAddress: "",
        baseLatitude: "",
        baseLongitude: "",
        useGoogleMaps: false,
        googleMapsApiKeyConfigured: false,
        googleMapsBrowserKey: "",
        googleMapsMapId: "",
      },
      publishingForm: {},
      deliveryForm: {
        feeText: "R$ 7,00",
        minimumText: "R$ 25,00",
        minMinutes: 35,
        maxMinutes: 50,
        deliveryEnabled: true,
        useGoogleMaps: false,
        baseAddress: "",
        baseLatitude: "",
        baseLongitude: "",
        pickupEnabled: true,
        googleMapsApiKey: "",
        googleMapsApiKeyConfigured: false,
        googleMapsBrowserKey: "",
        googleMapsMapId: "",
        pickupMinutes: 25,
        pickupMessage: "Retire no balcao informando o codigo do pedido.",
        costMode: "fixed",
        areaName: "Area padrao",
        radiusMinKm: 0,
        radiusMaxKm: null,
        blockedArea: false,
        freeAboveText: "R$ 0,00",
        costNote: "Taxa fixa aplicada para bairros atendidos.",
      },
      zoneForm: {
        open: false,
        id: null,
        nome: "Zona de entrega",
        bairro: "",
        cepInicio: "",
        cepFim: "",
        feeText: "R$ 7,00",
        minimumText: "R$ 0,00",
        freeAboveText: "R$ 0,00",
        minMinutes: null,
        maxMinutes: null,
        bloqueada: false,
      },
      checkoutForm: {
        pix: true,
        cash: true,
        card: true,
        message:
          "Revise seus dados antes de finalizar. Valores serao conferidos pelo sistema.",
      },
      promotionForm: {
        id: null,
        preset: "banner",
        name: "Promoção do cardápio",
        subtitle: "",
        description: "",
        targetType: "produto",
        productId: null,
        categoryId: null,
        discountType: "valor",
        discountText: "R$ 0,00",
        discountPercent: 0,
        previousPriceText: "R$ 0,00",
        promotionalPriceText: "R$ 0,00",
        bannerText: "",
        giftText: "",
        imageUrl: "",
        imageFile: null,
        imagePreviewUrl: null,
        start: "",
        end: "",
        weekdays: [],
        enabled: true,
      },
      promotionPresetOptions: [
        {
          value: "banner",
          label: "Banner promocional",
          description: "Campanha visual sem desconto obrigatório.",
        },
        {
          value: "preco_especial",
          label: "Preço especial",
          description: "Produto com preço anterior e preço promocional.",
        },
        {
          value: "combo",
          label: "Combo",
          description: "Oferta de itens juntos com preço fechado.",
        },
        {
          value: "compre_ganhe",
          label: "Compre e ganhe",
          description: "Brinde ou benefício comunicado na vitrine.",
        },
        {
          value: "dia_semana",
          label: "Dia da semana",
          description: "Promoção válida somente nos dias marcados.",
        },
        {
          value: "cupom",
          label: "Cupom",
          description: "Chamada para uso de código no checkout.",
        },
      ],
      couponForm: {
        id: null,
        code: "",
        name: "",
        discountType: "valor",
        discountText: "R$ 0,00",
        discountPercent: 0,
        minimumText: "R$ 0,00",
        totalLimit: null,
        start: "",
        end: "",
        enabled: true,
      },
      comboForm: {
        id: null,
        productId: null,
        name: "",
        description: "",
        priceText: "R$ 0,00",
        itemsText: "",
        enabled: true,
      },
      operationForm: {
        manualAccept: true,
        printOrder: true,
        sound: true,
        note: "Conferir pedido, aceitar e acompanhar ate a entrega ou retirada.",
      },
      visualForm: {
        logoUrl: "/media/logos/logo_nuts.png",
        capaUrl: "",
        logoFile: null,
        capaFile: null,
        logoPreviewUrl: null,
        capaPreviewUrl: null,
        primaryColor: "#ee7361",
        accentColor: "#ffd993",
        backgroundColor: "#fff8f6",
        cardStyle: "soft",
        buttonStyle: "rounded",
        emptyText: "Nenhum produto encontrado.",
        errorText: "Nao foi possivel carregar o cardapio.",
        closedText: "Estamos fechados agora. Consulte o proximo horario.",
        checkoutText:
          "Revise seus dados antes de finalizar. Valores serao conferidos pelo sistema.",
        trackingText: "Acompanhe atualizacoes do seu pedido por aqui.",
      },
      visualPreviewSrc: "",
      visualPreviewTimer: null,
      cardStyleOptions: [
        {
          value: "soft",
          label: "Cards suaves",
          description: "Bordas leves e sombra discreta.",
        },
        {
          value: "flat",
          label: "Cards limpos",
          description: "Menos sombra e mais foco no conteúdo.",
        },
      ],
      buttonStyleOptions: [
        {
          value: "rounded",
          label: "Botões arredondados",
          description: "Visual acolhedor e comercial.",
        },
        {
          value: "pill",
          label: "Botões cápsula",
          description: "Mais destaque em ações rápidas.",
        },
      ],
      sections: [
        {
          id: "overview",
          label: "Visao geral",
          description: "Resumo do site",
          kicker: "Resumo",
          longDescription:
            "Veja status, link, logo, produtos e alertas principais do cardapio.",
          actionLabel: "Ver site",
        },
        {
          id: "publishing",
          label: "Publicacao",
          description: "Link, status e horarios",
          kicker: "Publicacao",
          longDescription:
            "Controle nome, link, abertura da loja, horarios e aviso de loja fechada.",
          actionLabel: "Editar publicação",
        },
        {
          id: "catalog",
          label: "Catalogo",
          description: "Categorias e produtos",
          kicker: "Catalogo",
          longDescription:
            "Confira produtos reais marcados para restaurante e organize a apresentacao.",
          actionLabel: "Cadastrar produto",
        },
        {
          id: "options",
          label: "Opções e adicionais",
          description: "Globais do cardápio",
          kicker: "Adicionais",
          longDescription:
            "Configure aqui apenas opções que devem valer para todo o cardápio ou categorias inteiras. Opções de um item ficam dentro do produto.",
          actionLabel: "Criar grupo global",
        },
        {
          id: "pizzas",
          label: "Variantes",
          description: "Tamanhos e sabores",
          kicker: "Variantes",
          longDescription:
            "Configure produtos com tamanho, múltiplos sabores e disponibilidade.",
          actionLabel: "Nova variante",
        },
        {
          id: "promotions",
          label: "Promocoes",
          description: "Destaques e periodo",
          kicker: "Promocoes",
          longDescription:
            "Configure destaques, banners curtos e periodo visual de promocao.",
          actionLabel: "Configurar promocoes",
        },
        {
          id: "delivery",
          label: "Entrega",
          description: "Taxa e tempo",
          kicker: "Entrega",
          longDescription:
            "Configure taxa fixa, pedido minimo e prazo. Ative Google API apenas quando o restaurante quiser calcular por ponto e raio.",
          actionLabel: "Configurar entrega",
        },
        {
          id: "pickup",
          label: "Retirada",
          description: "Balcao e preparo",
          kicker: "Retirada",
          longDescription:
            "Configure retirada no balcao e orientacoes para o cliente.",
          actionLabel: "Configurar retirada",
        },
        {
          id: "checkout",
          label: "Pagamento",
          description: "Motoboy e balcão",
          kicker: "Checkout",
          longDescription:
            "Defina formas de pagamento, troco e mensagens do fechamento.",
          actionLabel: "Configurar checkout",
        },
        {
          id: "operation",
          label: "Operacao",
          description: "Pedidos e impressao",
          kicker: "Operacao",
          longDescription:
            "Prepare a equipe para aceitar, imprimir e acompanhar pedidos online.",
          actionLabel: "Configurar operacao",
        },
        {
          id: "visual",
          label: "Visual e textos",
          description: "Logo, cores e avisos",
          kicker: "Visual",
          longDescription:
            "Ajuste logo, cores de referencia e mensagens que aparecem ao cliente.",
          actionLabel: "Editar visual",
        },
      ],
      categories: [],
      products: [],
      optionGroups: [],
      pizzas: [],
      areasEntrega: [],
      promocoes: [],
      cupons: [],
      combos: [],
      textosPublicos: [],
    };
  },
  computed: {
    currentSection() {
      return (
        this.sections.find((section) => section.id === this.activeSection) ||
        this.sections[0]
      );
    },
    currentCards() {
      return SECTION_CARDS[this.activeSection] || [];
    },
    logoUrl() {
      return this.visualForm.logoUrl || "/media/logos/logo_nuts.png";
    },
    visualLogoPreview() {
      return this.visualForm.logoPreviewUrl || this.visualForm.logoUrl;
    },
    visualCoverPreview() {
      return this.visualForm.capaPreviewUrl || this.visualForm.capaUrl;
    },
    visualPreviewStyle() {
      return {
        "--preview-primary": this.normalizedHexColor(
          this.visualForm.primaryColor,
          "#ee7361"
        ),
        "--preview-accent": this.normalizedHexColor(
          this.visualForm.accentColor,
          "#ffd993"
        ),
        "--preview-background": this.normalizedHexColor(
          this.visualForm.backgroundColor,
          "#fff8f6"
        ),
        "--preview-card-radius":
          this.visualForm.cardStyle === "flat" ? "6px" : "12px",
        "--preview-button-radius":
          this.visualForm.buttonStyle === "pill" ? "999px" : "10px",
      };
    },
    visualCoverPreviewStyle() {
      if (this.visualCoverPreview) {
        return {
          backgroundImage: `linear-gradient(90deg, rgba(255,255,255,.92), rgba(255,255,255,.72)), url(${this.visualCoverPreview})`,
        };
      }

      return {};
    },
    visualContrastWarning() {
      const contrast = this.colorContrast(
        this.visualForm.primaryColor,
        "#ffffff"
      );

      if (contrast > 0 && contrast < 3) {
        return "A cor principal esta com pouco contraste para texto branco nos botoes.";
      }

      return "";
    },
    promotionImagePreview() {
      return this.promotionForm.imagePreviewUrl || this.promotionForm.imageUrl;
    },
    availableProducts() {
      return this.products.filter((product) => product.available).length;
    },
    publishedProducts() {
      return this.products.filter((product) => product.id);
    },
    metrics() {
      return [
        {
          label: "Produtos no site",
          value: this.products.length,
          hint: `${this.availableProducts} disponiveis para o cliente`,
        },
        {
          label: "Categorias",
          value: this.categories.length,
          hint: "Organizacao do cardapio",
        },
        {
          label: "Pedido minimo",
          value: this.formatMoney(this.config.minimumOrderCents),
          hint: "Valor conferido ao finalizar",
        },
        {
          label: "Entrega",
          value: `${this.config.minMinutes} a ${this.config.maxMinutes} min`,
          hint: `${this.formatMoney(this.config.deliveryFeeCents)} de taxa`,
        },
      ];
    },
    publicSurface() {
      return [
        "Link do cardapio",
        "Loja aberta/pausada",
        "Horarios",
        "Categorias",
        "Produtos",
        "Fotos",
        "Busca",
        "Carrinho",
        "Entrega",
        "Retirada",
        "Pedido minimo",
        "Taxa de entrega",
        "Checkout",
        "Pix presencial",
        "Pagamento presencial",
        "Acompanhamento",
        "Mensagens de erro",
      ];
    },
    pendingAlerts() {
      const alerts = [];

      if (!this.config.habilitado) {
        alerts.push("Cardapio pausado para o cliente.");
      }

      if (!this.products.length) {
        alerts.push("Nenhum produto marcado para restaurante.");
      }

      if (this.products.some((product) => !product.imageUrl)) {
        alerts.push("Alguns produtos ainda estao sem foto.");
      }

      if (!alerts.length) {
        alerts.push("Dados principais prontos para demonstracao.");
      }

      return alerts;
    },
    hasFulfillmentMode() {
      return Boolean(this.config.deliveryEnabled || this.config.pickupEnabled);
    },
    canPublishCardapio() {
      return (
        this.products.length > 0 &&
        this.availableProducts > 0 &&
        this.hasFulfillmentMode
      );
    },
    publicationStatusLabel() {
      if (!this.config.habilitado) {
        return "Pausado";
      }

      if (!this.products.length) {
        return "Sem produtos";
      }

      if (!this.availableProducts) {
        return "Sem itens disponíveis";
      }

      if (!this.hasFulfillmentMode) {
        return "Sem entrega ou retirada";
      }

      return "Publicado";
    },
    publicationStatusClass() {
      if (!this.config.habilitado) {
        return "label-light-danger";
      }

      if (!this.canPublishCardapio) {
        return "label-light-warning";
      }

      return "label-light-success";
    },
    publicationStatusDescription() {
      if (!this.config.habilitado) {
        return this.config.closedMessage || "O cliente vê o cardápio pausado.";
      }

      if (!this.products.length) {
        return "Cadastre ou publique produtos antes de abrir o cardápio para pedidos.";
      }

      if (!this.availableProducts) {
        return "Ative pelo menos um produto para o cliente conseguir comprar.";
      }

      if (!this.hasFulfillmentMode) {
        return "Ative entrega ou retirada para liberar o checkout.";
      }

      return "O cardápio está publicado e pronto para receber pedidos.";
    },
    publicationChecks() {
      return [
        this.products.length
          ? `${this.products.length} produtos publicados`
          : "Nenhum produto publicado",
        this.availableProducts
          ? `${this.availableProducts} produtos disponíveis`
          : "Nenhum produto disponível",
        this.hasFulfillmentMode
          ? "Entrega ou retirada ativa"
          : "Entrega e retirada desativadas",
        this.config.scheduleLabel || "Horário não informado",
      ];
    },
    visiblePauseOptions() {
      if (this.pauseForm.type === "cardapio") {
        return this.pauseOptions.filter((option) => option.value === "manual");
      }

      return this.pauseOptions;
    },
    globalOptionGroups() {
      return this.optionGroups.filter((group) => !group.cardapio_produto_id);
    },
    optionGroupModalTitle() {
      if (this.optionGroupForm?.mode === "produto") {
        return "Opções deste produto";
      }

      return "Grupo global do cardápio";
    },
    optionGroupModalSubtitle() {
      if (this.optionGroupForm?.mode === "produto") {
        return "Grupo exclusivo do produto";
      }

      return "Grupo global";
    },
    optionGroupModalDescription() {
      if (this.optionGroupForm?.mode === "produto") {
        return "Use para escolhas que pertencem somente a este item, como borda, ponto, molho ou remoção de ingrediente.";
      }

      return "Use quando a mesma escolha deve aparecer em todo o cardápio ou em categorias inteiras, como talheres, embalagem, molhos gerais ou observações.";
    },
    optionGroupTypeHelp() {
      if (!this.optionGroupForm) {
        return "";
      }

      const helps = {
        escolha_unica: "Cliente escolhe uma opção, como massa ou borda.",
        multipla_escolha: "Cliente pode marcar mais de uma opção.",
        adicional_pago: "Itens pagos somam ao valor final.",
        remocao_sem_preco: "Opções para retirar ingredientes sem cobrar.",
        observacao_textual: "Mostra um campo de texto para instruções do item.",
        acompanhamento_obrigatorio:
          "Cliente precisa escolher acompanhamento antes de comprar.",
      };

      return helps[this.optionGroupForm.tipo] || "";
    },
    optionGroupItemHint() {
      if (!this.optionGroupForm) {
        return "Cadastre as opções que o cliente poderá escolher.";
      }

      if (this.optionGroupForm.tipo === "observacao_textual") {
        return "Este tipo mostra um campo de texto no produto. Itens abaixo são opcionais.";
      }

      return "Cadastre as opções que o cliente poderá escolher no produto.";
    },
    nextChangeLabel() {
      return this.config.habilitado
        ? "Fechamento conforme horario informado."
        : this.config.closedMessage;
    },
    deliveryPreviewLabel() {
      const feeCents = this.parseMoneyToCents(this.deliveryForm.feeText);
      const minimumCents = this.parseMoneyToCents(
        this.deliveryForm.minimumText
      );

      return `Taxa: ${this.formatMoney(
        feeCents
      )} | Pedido minimo: ${this.formatMoney(minimumCents)}`;
    },
    deliveryZones() {
      return (this.areasEntrega || []).filter(
        (area) => area.raio_km_max === null || area.raio_km_max === undefined
      );
    },
    deliveryBaseMapUrl() {
      return this.googleMapEmbedUrl(
        this.deliveryForm.baseLatitude,
        this.deliveryForm.baseLongitude,
        this.deliveryForm.baseAddress
      );
    },
    deliveryRadiusMapUrl() {
      return this.deliveryBaseMapUrl;
    },
    googleMapsBrowserKey() {
      return (
        this.deliveryForm.googleMapsBrowserKey ||
        this.config.googleMapsBrowserKey ||
        process.env.VUE_APP_GOOGLE_MAPS_BROWSER_KEY ||
        ""
      );
    },
  },
  watch: {
    visualForm: {
      deep: true,
      handler() {
        this.scheduleVisualPreviewPush();
      },
    },
  },
  async mounted() {
    const ativo = await this.ensureCardapioOnlineAtivo();

    if (ativo) {
      await this.loadManagement();
    }
  },
  beforeDestroy() {
    if (this.visualPreviewTimer) {
      clearTimeout(this.visualPreviewTimer);
    }
  },
  methods: {
    async ensureCardapioOnlineAtivo() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracao = Array.isArray(data) ? data[0] : data;

        if (configuracao?.restaurante_cardapio_online_ativo) {
          return true;
        }
      } catch (error) {
        this.$toast.error(
          "Nao foi possivel validar a configuracao do Cardapio Online."
        );
      }

      this.$toast.warning(
        "Ative o Cardapio Online em Configuracoes > Restaurante para acessar esta gestao."
      );
      this.$router.push("/gerais");
      return false;
    },
    googleMapEmbedUrl(latitude, longitude, address) {
      const hasCoordinates =
        latitude !== null &&
        latitude !== undefined &&
        latitude !== "" &&
        longitude !== null &&
        longitude !== undefined &&
        longitude !== "";
      const query = hasCoordinates ? `${latitude},${longitude}` : address;

      if (!query) {
        return "";
      }

      return `https://www.google.com/maps?q=${encodeURIComponent(
        query
      )}&output=embed`;
    },
    loadGoogleMapsBrowser() {
      if (!this.googleMapsBrowserKey) {
        return Promise.reject(
          new Error("Chave publica do Google Maps nao configurada.")
        );
      }

      if (window.google && window.google.maps) {
        return Promise.resolve(window.google);
      }

      if (this._googleMapsPromise) {
        return this._googleMapsPromise;
      }

      this._googleMapsPromise = new Promise((resolve, reject) => {
        const callbackName = "__cardapioVue2GoogleMapsReady";
        const existingScript = document.getElementById(
          "cardapio-vue2-google-maps"
        );

        window[callbackName] = () => resolve(window.google);

        if (existingScript) {
          return;
        }

        const script = document.createElement("script");
        script.id = "cardapio-vue2-google-maps";
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
          this.googleMapsBrowserKey
        )}&v=weekly&callback=${callbackName}`;
        script.onerror = () => {
          this._googleMapsPromise = null;
          reject(new Error("Nao foi possivel carregar o Google Maps."));
        };
        document.head.appendChild(script);
      });

      return this._googleMapsPromise;
    },
    async resolveDeliveryBasePoint(google) {
      const lat = Number(this.deliveryForm.baseLatitude);
      const lng = Number(this.deliveryForm.baseLongitude);

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }

      if (this.deliveryForm.baseAddress) {
        try {
          const geocoder = new google.maps.Geocoder();
          const response = await geocoder.geocode({
            address: this.deliveryForm.baseAddress,
          });
          const location = response.results[0]?.geometry?.location;

          if (location) {
            const point = { lat: location.lat(), lng: location.lng() };
            this.setDeliveryBasePoint(point.lat, point.lng);
            return point;
          }
        } catch (error) {
          this.$toast.warning(
            "Nao foi possivel localizar o endereco base no mapa."
          );
        }
      }

      return { lat: -23.55052, lng: -46.633308 };
    },
    setDeliveryBasePoint(latitude, longitude) {
      this.deliveryForm.baseLatitude = Number(latitude).toFixed(7);
      this.deliveryForm.baseLongitude = Number(longitude).toFixed(7);
    },
    drawDeliveryMap(google, element, point, showRadius) {
      if (!element) {
        return;
      }

      const map = new google.maps.Map(element, {
        center: point,
        zoom: showRadius ? 12 : 16,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      });

      const marker = new google.maps.Marker({
        map,
        position: point,
        title: "Base de entrega",
      });

      if (showRadius && Number(this.deliveryForm.radiusMaxKm) > 0) {
        new google.maps.Circle({
          map,
          center: point,
          radius: Number(this.deliveryForm.radiusMaxKm) * 1000,
          strokeColor: "#ee7361",
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: "#ee7361",
          fillOpacity: 0.12,
        });
      }

      map.addListener("click", (event) => {
        if (!event.latLng) {
          return;
        }

        const nextPoint = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

        this.setDeliveryBasePoint(nextPoint.lat, nextPoint.lng);
        marker.setPosition(nextPoint);
        map.panTo(nextPoint);

        if (showRadius) {
          this.renderDeliveryMaps();
        }
      });
    },
    renderDeliveryMaps() {
      if (!this.deliveryForm.useGoogleMaps || !this.googleMapsBrowserKey) {
        return;
      }

      this.$nextTick(async () => {
        try {
          const google = await this.loadGoogleMapsBrowser();
          const point = await this.resolveDeliveryBasePoint(google);

          this.drawDeliveryMap(
            google,
            this.$refs.deliveryBaseMap,
            point,
            false
          );
          this.drawDeliveryMap(
            google,
            this.$refs.deliveryRadiusMap,
            point,
            true
          );
        } catch (error) {
          this.$toast.warning(
            error && error.message
              ? error.message
              : "Nao foi possivel abrir o mapa."
          );
        }
      });
    },
    async loadManagement() {
      this.managementLoading = true;

      try {
        const { data } = await this.$http.get(
          "/restaurante/cardapio-online/gestao"
        );
        this.applyManagementData(this.unwrapResponse(data) || {});
      } catch (error) {
        this.$toast.warning(
          "Nao foi possivel carregar os dados do Cardapio Online."
        );
      } finally {
        this.managementLoading = false;
      }
    },
    applyManagementData(data) {
      const config = data.configuracao || {};

      this.config = {
        slug: config.slug || this.config.slug,
        publicUrl: config.public_url || this.config.publicUrl,
        name: config.nome_publico || this.config.name,
        description: config.descricao || this.config.description,
        habilitado: Boolean(config.habilitado),
        deliveryEnabled: Boolean(config.aceita_entrega),
        pickupEnabled: Boolean(config.aceita_retirada),
        scheduleLabel: config.horario_resumo || this.config.scheduleLabel,
        closedMessage: config.mensagem_fechado || this.config.closedMessage,
        minimumOrderCents:
          config.pedido_minimo_centavos ?? this.config.minimumOrderCents,
        deliveryFeeCents:
          config.taxa_entrega_padrao_centavos ?? this.config.deliveryFeeCents,
        minMinutes: config.tempo_entrega_minutos_min ?? this.config.minMinutes,
        maxMinutes: config.tempo_entrega_minutos_max ?? this.config.maxMinutes,
        baseAddress: config.endereco_base || this.config.baseAddress,
        baseLatitude:
          config.latitude_base !== null && config.latitude_base !== undefined
            ? config.latitude_base
            : this.config.baseLatitude,
        baseLongitude:
          config.longitude_base !== null && config.longitude_base !== undefined
            ? config.longitude_base
            : this.config.baseLongitude,
        useGoogleMaps: Boolean(config.usar_google_maps_entrega),
        googleMapsApiKeyConfigured: Boolean(
          config.google_maps_api_key_configurada
        ),
        googleMapsBrowserKey: config.google_maps_browser_key || "",
        googleMapsMapId: config.google_maps_map_id || "",
      };

      this.visualForm.logoUrl = config.logo_url || this.visualForm.logoUrl;
      this.visualForm.capaUrl = config.capa_url || this.visualForm.capaUrl;
      this.syncFormsFromConfig();

      this.categories = (data.categorias || []).map(this.normalizeCategory);

      this.products = (data.produtos || []).map(this.normalizeProduct);
      this.optionGroups = (data.opcoes || []).map(this.normalizeOptionGroup);
      this.pizzas = (data.pizzas || []).map(this.normalizePizza);
      this.areasEntrega = data.areas_entrega || [];
      this.promocoes = data.promocoes || [];
      this.cupons = data.cupons || [];
      this.combos = data.combos || [];
      this.textosPublicos = data.textos || [];
      this.applyOperationData(data.operacao || {});
      this.applyVisualData(data.visual || {}, data.textos || []);

      if (!this.promotionForm.productId && this.publishedProducts.length) {
        this.promotionForm.productId = this.publishedProducts[0].id;
      }

      if (!this.comboForm.productId && this.publishedProducts.length) {
        this.comboForm.productId = this.publishedProducts[0].id;
      }
    },
    unwrapResponse(response) {
      if (
        response &&
        response.success === true &&
        response.data !== undefined
      ) {
        return response.data;
      }

      return response;
    },
    isSaving(key) {
      return Boolean(this.saving[key]);
    },
    setSaving(key, value) {
      this.$set(this.saving, key, value);
    },
    clearFieldErrors() {
      this.fieldErrors = {};
    },
    setFieldErrors(errors) {
      this.fieldErrors = Object.keys(errors || {}).reduce((carry, field) => {
        const value = errors[field];
        carry[field] = Array.isArray(value) ? value[0] : value;
        return carry;
      }, {});
    },
    fieldError(field) {
      return this.fieldErrors[field] || "";
    },
    showApiError(error, fallbackMessage) {
      const data = error.response?.data || {};
      const errors = data.errors || {};
      const firstField = Object.keys(errors)[0];
      const firstMessage = firstField ? errors[firstField][0] : null;

      this.setFieldErrors(errors);
      this.$toast.error(data.message || firstMessage || fallbackMessage);
    },
    normalizeCategory(category) {
      const available =
        category.disponivel_efetivo !== undefined
          ? Boolean(category.disponivel_efetivo)
          : Boolean(category.ativo !== false && category.visivel !== false);

      return {
        id: category.id || category.codigo,
        slug: category.codigo || category.slug,
        name: category.nome,
        count: category.quantidade_produtos || 0,
        available,
        pausedUntil: category.pausado_ate || null,
      };
    },
    applyOperationData(operacao) {
      const formasPagamento = operacao.formas_pagamento || {};
      const mensagens = operacao.mensagens_cliente || {};
      const regras = operacao.regras || {};

      this.checkoutForm.pix = formasPagamento.pix !== false;
      this.checkoutForm.cash = formasPagamento.dinheiro !== false;
      this.checkoutForm.card = formasPagamento.cartao !== false;
      this.checkoutForm.message =
        mensagens.checkout || this.checkoutForm.message;

      this.operationForm.manualAccept = operacao.aceite_manual !== false;
      this.operationForm.printOrder = Boolean(operacao.impressao_automatica);
      this.operationForm.sound = regras.sound !== false;
      this.operationForm.note = mensagens.operacao || this.operationForm.note;
    },
    applyVisualData(visual, textos) {
      this.visualForm.logoUrl = visual.logo_url || this.visualForm.logoUrl;
      this.visualForm.capaUrl = visual.capa_url || this.visualForm.capaUrl;
      this.visualForm.logoFile = null;
      this.visualForm.capaFile = null;
      this.clearVisualPreview("logo");
      this.clearVisualPreview("capa");
      this.visualForm.primaryColor =
        visual.primary_color || this.visualForm.primaryColor;
      this.visualForm.accentColor =
        visual.accent_color || this.visualForm.accentColor;
      this.visualForm.backgroundColor =
        visual.background_color || this.visualForm.backgroundColor;
      this.visualForm.cardStyle =
        visual.card_style || this.visualForm.cardStyle;
      this.visualForm.buttonStyle =
        visual.button_style || this.visualForm.buttonStyle;

      const emptyText = (textos || []).find(
        (texto) => texto.chave === "sem_produtos"
      );
      const errorText = (textos || []).find(
        (texto) => texto.chave === "erro_carregamento"
      );
      const closedText = (textos || []).find(
        (texto) => texto.chave === "loja_fechada"
      );
      const checkoutText = (textos || []).find(
        (texto) => texto.chave === "checkout"
      );
      const trackingText = (textos || []).find(
        (texto) => texto.chave === "acompanhamento"
      );

      this.visualForm.emptyText =
        emptyText?.conteudo || this.visualForm.emptyText;
      this.visualForm.errorText =
        errorText?.conteudo || this.visualForm.errorText;
      this.visualForm.closedText =
        closedText?.conteudo || this.visualForm.closedText;
      this.visualForm.checkoutText =
        checkoutText?.conteudo || this.visualForm.checkoutText;
      this.visualForm.trackingText =
        trackingText?.conteudo || this.visualForm.trackingText;
    },
    normalizeProduct(product) {
      const name = product.nome || product.descricao || "Produto";
      const published = Boolean(product.publicado);

      return {
        id: published ? product.id : null,
        sourceId: product.id,
        productId: product.produto_id || product.id,
        code: product.codigo,
        type: product.tipo || "produto",
        slug: product.slug || this.slugify(name),
        name,
        description: product.descricao || "Sem descricao cadastrada.",
        categoryId: product.categoria_id,
        categoryName: product.categoria_nome,
        priceCents: product.preco_centavos || 0,
        initials: this.initials(name),
        tag: product.disponivel ? "Disponivel" : "Pausado",
        published,
        highlighted: false,
        available:
          product.disponivel_efetivo !== undefined
            ? Boolean(product.disponivel_efetivo)
            : Boolean(product.disponivel),
        rawAvailable: Boolean(product.disponivel),
        pausedUntil: product.pausado_ate || null,
        imageUrl: product.imagem_url,
        noPreparation: Boolean(product.sem_preparo),
        hasPizza: Boolean(product.tem_pizza),
      };
    },
    normalizePizza(pizza) {
      return Object.assign({}, pizza, {
        tamanhos: (pizza.tamanhos || []).map((size) =>
          Object.assign({}, size, {
            precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
            localKey: size.id || `${Date.now()}-${Math.random()}`,
          })
        ),
        sabores: (pizza.sabores || []).map((flavor) =>
          Object.assign({}, flavor, {
            precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
            tamanhos: flavor.tamanhos || [],
            localKey: flavor.id || `${Date.now()}-${Math.random()}`,
          })
        ),
      });
    },
    normalizeOptionGroup(group) {
      const regras = group.regras || {};
      const categoriaIds = Array.isArray(regras.categorias)
        ? regras.categorias
        : [];

      return Object.assign({}, group, {
        regras,
        categoria_ids: categoriaIds,
        opcoes: (group.opcoes || []).map((option) =>
          Object.assign({}, option, {
            precoText: this.moneyTextFromCents(
              option.preco_adicional_centavos || 0
            ),
            available:
              option.disponivel_efetivo !== undefined
                ? Boolean(option.disponivel_efetivo)
                : Boolean(option.disponivel),
            pausedUntil: option.pausado_ate || null,
            localKey: option.id || `${Date.now()}-${Math.random()}`,
          })
        ),
      });
    },
    syncFormsFromConfig() {
      this.publishingForm = {
        name: this.config.name,
        slug: this.config.slug,
        description: this.config.description,
        scheduleLabel: this.config.scheduleLabel,
        closedMessage: this.config.closedMessage,
      };

      this.deliveryForm.feeText = this.moneyTextFromCents(
        this.config.deliveryFeeCents
      );
      this.deliveryForm.minimumText = this.moneyTextFromCents(
        this.config.minimumOrderCents
      );
      this.deliveryForm.minMinutes = this.config.minMinutes;
      this.deliveryForm.maxMinutes = this.config.maxMinutes;
      this.deliveryForm.deliveryEnabled = this.config.deliveryEnabled;
      this.deliveryForm.useGoogleMaps = this.config.useGoogleMaps;
      this.deliveryForm.baseAddress = this.config.baseAddress;
      this.deliveryForm.baseLatitude = this.config.baseLatitude;
      this.deliveryForm.baseLongitude = this.config.baseLongitude;
      this.deliveryForm.googleMapsApiKey = "";
      this.deliveryForm.googleMapsApiKeyConfigured =
        this.config.googleMapsApiKeyConfigured;
      this.deliveryForm.googleMapsBrowserKey = this.config.googleMapsBrowserKey;
      this.deliveryForm.googleMapsMapId = this.config.googleMapsMapId;
      this.deliveryForm.pickupEnabled = this.config.pickupEnabled;

      const firstArea = this.areasEntrega[0] || {};
      this.deliveryForm.areaName = firstArea.nome || this.deliveryForm.areaName;
      this.deliveryForm.radiusMinKm =
        firstArea.raio_km_min !== undefined && firstArea.raio_km_min !== null
          ? firstArea.raio_km_min
          : this.deliveryForm.radiusMinKm;
      this.deliveryForm.radiusMaxKm =
        firstArea.raio_km_max !== undefined && firstArea.raio_km_max !== null
          ? firstArea.raio_km_max
          : this.deliveryForm.radiusMaxKm;
      this.deliveryForm.blockedArea = Boolean(firstArea.bloqueada);
      this.deliveryForm.freeAboveText = this.moneyTextFromCents(
        firstArea.frete_gratis_acima_centavos || 0
      );
    },
    selectSection(sectionId) {
      this.activeSection = sectionId;
    },
    categoryName(categoryId) {
      const category = this.categories.find((item) => item.id === categoryId);
      return category ? category.name : "Sem categoria";
    },
    productName(productId) {
      const product = this.products.find((item) => item.id === productId);
      return product ? product.name : "Grupo global";
    },
    optionGroupScopeLabel(group) {
      if (group.cardapio_produto_id) {
        return `Produto: ${this.productName(group.cardapio_produto_id)}`;
      }

      const categorias = group.regras?.categorias || group.categoria_ids || [];

      if (!categorias.length) {
        return "Todo o cardápio";
      }

      return `Categorias: ${categorias
        .map((id) => this.categoryName(id))
        .join(", ")}`;
    },
    productOptionGroups(product) {
      if (!product?.id) {
        return [];
      }

      return this.optionGroups.filter(
        (group) => group.cardapio_produto_id === product.id
      );
    },
    productPizzaTemplate(product) {
      if (!product?.id) {
        return null;
      }

      return (
        this.pizzas.find((pizza) => pizza.cardapio_produto_id === product.id) ||
        null
      );
    },
    optionTypeLabel(type) {
      const item = this.optionGroupTypes.find(
        (option) => option.value === type
      );
      return item ? item.label : "Opção";
    },
    formatMoney(cents) {
      return (Number(cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    moneyTextFromCents(cents) {
      return (Number(cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    parseMoneyToCents(value) {
      const normalized = String(value || "0")
        .replace(/[R$\s.]/g, "")
        .replace(",", ".");

      return Math.round(Number(normalized || 0) * 100);
    },
    goToNewProduct() {
      this.$router.push({ name: "produtosCreate" });
    },
    goToProducts() {
      this.$router.push({ name: "produtosLista" });
    },
    openPublicPreview() {
      window.open(this.config.publicUrl, "_blank");
    },
    buildVisualPreviewUrl() {
      const base = this.config.publicUrl || "";

      if (!base) {
        return "";
      }

      const separator = base.indexOf("?") === -1 ? "?" : "&";
      const params = [
        "preview=1",
        "primary=" +
          encodeURIComponent(
            this.normalizedHexColor(this.visualForm.primaryColor, "#ee7361")
          ),
        "accent=" +
          encodeURIComponent(
            this.normalizedHexColor(this.visualForm.accentColor, "#ffd993")
          ),
        "bg=" +
          encodeURIComponent(
            this.normalizedHexColor(this.visualForm.backgroundColor, "#fff8f6")
          ),
        "card=" + encodeURIComponent(this.visualForm.cardStyle || "soft"),
        "button=" + encodeURIComponent(this.visualForm.buttonStyle || "rounded"),
      ];

      return base + separator + params.join("&");
    },
    initVisualPreview() {
      this.visualPreviewSrc = this.buildVisualPreviewUrl();
    },
    pushVisualPreviewTheme() {
      const frame = this.$refs.visualPreviewFrame;

      if (!frame || !frame.contentWindow) {
        return;
      }

      frame.contentWindow.postMessage(
        {
          type: "cardapio-preview-theme",
          primary: this.normalizedHexColor(this.visualForm.primaryColor, "#ee7361"),
          accent: this.normalizedHexColor(this.visualForm.accentColor, "#ffd993"),
          background: this.normalizedHexColor(
            this.visualForm.backgroundColor,
            "#fff8f6"
          ),
          cardStyle: this.visualForm.cardStyle || "soft",
          buttonStyle: this.visualForm.buttonStyle || "rounded",
        },
        "*"
      );
    },
    scheduleVisualPreviewPush() {
      if (this.visualPreviewTimer) {
        clearTimeout(this.visualPreviewTimer);
      }

      this.visualPreviewTimer = setTimeout(() => {
        this.pushVisualPreviewTheme();
        this.visualPreviewTimer = null;
      }, 250);
    },
    openPrimaryAction() {
      const actions = {
        overview: this.openPublicPreview,
        publishing: this.openPublishingModal,
        catalog: this.goToNewProduct,
        options: () => this.openOptionGroupModal(),
        pizzas: () => this.openPizzaModal(),
        promotions: () => this.handleCardAction("promotions"),
        delivery: () => this.handleCardAction("delivery"),
        pickup: () => this.handleCardAction("pickup"),
        checkout: () => this.handleCardAction("checkout"),
        operation: () => this.handleCardAction("operation"),
        visual: () => this.handleCardAction("visual"),
      };

      const action = actions[this.activeSection] || this.openPublicPreview;
      action();
    },
    handleCardAction(action) {
      const modals = {
        delivery: "modalCardapioDelivery",
        pickup: "modalCardapioPickup",
        checkout: "modalCardapioCheckout",
        operation: "modalCardapioOperation",
        visual: "modalCardapioVisual",
        promotions: "modalCardapioPromotions",
      };

      if (action === "preview") {
        this.openPublicPreview();
        return;
      }

      if (modals[action]) {
        this.$bvModal.show(modals[action]);
      }
    },
    openPublishingModal() {
      this.syncFormsFromConfig();
      this.$bvModal.show("modalCardapioPublishing");
    },
    openCardapioPauseModal() {
      this.pauseForm = {
        type: "cardapio",
        target: null,
        typeLabel: "Cardápio inteiro",
        name: this.config.name,
        mode: "manual",
        customUntil: "",
        reason: "",
        message:
          this.config.closedMessage ||
          "Estamos fechados agora. Consulte o próximo horário.",
        savingKey: "config",
      };
      this.$bvModal.show("modalCardapioPause");
    },
    openProductModal(product) {
      if (!product) {
        this.$toast.warning(
          "Cadastre ou marque um produto para restaurante primeiro."
        );
        return;
      }

      this.productForm = Object.assign({}, product, {
        activeTab: "data",
        priceText: this.moneyTextFromCents(product.priceCents),
        imageFile: null,
        imagePreviewUrl: null,
      });
      this.$bvModal.show("modalCardapioProduct");
    },
    findOptionGroupPreset(key) {
      return this.optionGroupPresets.find((preset) => preset.key === key);
    },
    optionGroupPresetMatches(preset) {
      return (
        this.optionGroupForm &&
        this.optionGroupForm.nome === preset.name &&
        this.optionGroupForm.tipo === preset.type
      );
    },
    applyOptionGroupPreset(key) {
      if (!this.optionGroupForm) {
        return;
      }

      const preset = this.findOptionGroupPreset(key);

      if (!preset) {
        return;
      }

      this.optionGroupForm.nome = preset.name;
      this.optionGroupForm.tipo = preset.type;
      this.optionGroupForm.minimo = preset.min;
      this.optionGroupForm.maximo = preset.max;
      this.optionGroupForm.obrigatorio = preset.required;
      this.optionGroupForm.permite_quantidade = preset.quantity;

      if (
        !this.optionGroupForm.opcoes.length ||
        this.optionGroupForm.id === null
      ) {
        this.optionGroupForm.opcoes = preset.items.map((item, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          produto_id: null,
          nome: item.name,
          descricao: item.description,
          precoText: this.moneyTextFromCents(item.price),
          ativo: true,
          disponivel: true,
          ordem: index,
        }));
      }
    },
    openOptionGroupModal(group = null, targetProduct = null, presetKey = null) {
      const mode =
        targetProduct || group?.cardapio_produto_id ? "produto" : "global";

      if (!group && mode === "produto" && !targetProduct?.id) {
        this.$toast.warning(
          "Publique um produto no site antes de criar opções."
        );
        return;
      }

      const regras = group?.regras || {};
      const categoriaIds = Array.isArray(regras.categorias)
        ? regras.categorias
        : [];

      this.optionGroupForm = group
        ? {
            id: group.id,
            cardapio_produto_id: group.cardapio_produto_id,
            mode,
            globalScope: categoriaIds.length ? "categorias" : "todos",
            categoria_ids: categoriaIds,
            nome: group.nome,
            tipo: group.tipo,
            minimo: group.minimo,
            maximo: group.maximo,
            obrigatorio: Boolean(group.obrigatorio),
            permite_quantidade: Boolean(group.permite_quantidade),
            ativo: Boolean(group.ativo),
            ordem: group.ordem || 0,
            opcoes: (group.opcoes || []).map((option) => ({
              id: option.id,
              localKey: option.id || `${Date.now()}-${Math.random()}`,
              produto_id: option.produto_id || null,
              nome: option.nome,
              descricao: option.descricao,
              precoText: this.moneyTextFromCents(
                option.preco_adicional_centavos || 0
              ),
              ativo: option.ativo !== false,
              disponivel: option.disponivel !== false,
              ordem: option.ordem || 0,
            })),
          }
        : {
            id: null,
            cardapio_produto_id: mode === "produto" ? targetProduct.id : null,
            mode,
            globalScope: "todos",
            categoria_ids: [],
            nome: "",
            tipo: "multipla_escolha",
            minimo: 0,
            maximo: 1,
            obrigatorio: false,
            permite_quantidade: false,
            ativo: true,
            ordem: this.optionGroups.length,
            opcoes: [],
          };

      if (!group && presetKey) {
        this.applyOptionGroupPreset(presetKey);
      }

      this.$bvModal.show("modalCardapioOptionGroup");
    },
    openProductOptionGroupModal(product, presetKey = null) {
      if (!product?.id) {
        this.$toast.warning("Salve o produto no site antes de criar opções.");
        return;
      }

      this.openOptionGroupModal(null, product, presetKey);
    },
    openOptionProductPicker() {
      if (!this.publishedProducts.length) {
        this.$toast.warning(
          "Publique um produto no site antes de criar opções específicas."
        );
        return;
      }

      this.$bvModal.show("modalCardapioOptionProductPicker");
    },
    selectProductForOptionGroup(product) {
      this.$bvModal.hide("modalCardapioOptionProductPicker");
      this.openProductOptionGroupModal(product);
    },
    openProductPizzaModal(product) {
      if (!product?.id) {
        this.$toast.warning(
          "Salve o produto no site antes de ativar variante."
        );
        return;
      }

      this.openPizzaModal(this.productPizzaTemplate(product), product);
    },
    openProductPromotionModal(product) {
      if (product?.id) {
        this.openPromotionModal(
          this.promocoes.find((item) => item.cardapio_produto_id === product.id)
        );
        this.promotionForm.productId = product.id;
        return;
      }

      this.$bvModal.show("modalCardapioPromotions");
    },
    openPizzaModal(pizza = null, targetProduct = null) {
      const firstProduct = targetProduct || this.publishedProducts[0] || null;

      if (!pizza && !firstProduct) {
        this.$toast.warning(
          "Cadastre ou publique um produto antes da variante."
        );
        return;
      }

      this.pizzaForm = pizza
        ? {
            id: pizza.id,
            cardapio_produto_id: pizza.cardapio_produto_id,
            nome_template: pizza.nome_template,
            regra_preco: pizza.regra_preco || "maior_sabor",
            max_sabores_padrao: pizza.max_sabores_padrao || 2,
            ativo: pizza.ativo !== false,
            tamanhos: (pizza.tamanhos || []).map((size) => ({
              id: size.id,
              localKey: size.id || `${Date.now()}-${Math.random()}`,
              nome: size.nome,
              slug: size.slug,
              precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
              max_sabores: size.max_sabores || 2,
              fatias: size.fatias || null,
              ativo: size.ativo !== false,
              ordem: size.ordem || 0,
            })),
            sabores: (pizza.sabores || []).map((flavor) => ({
              id: flavor.id,
              localKey: flavor.id || `${Date.now()}-${Math.random()}`,
              produto_id: flavor.produto_id || null,
              nome: flavor.nome,
              slug: flavor.slug,
              descricao: flavor.descricao,
              precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
              ativo: flavor.ativo !== false,
              disponivel: flavor.disponivel !== false,
              ordem: flavor.ordem || 0,
              tamanhos: flavor.tamanhos || [],
            })),
          }
        : {
            id: null,
            cardapio_produto_id: firstProduct.id,
            nome_template: firstProduct.name,
            regra_preco: "maior_sabor",
            max_sabores_padrao: 2,
            ativo: true,
            tamanhos: [],
            sabores: [],
          };

      if (!this.pizzaForm.tamanhos.length) {
        this.addPizzaSize();
      }

      if (!this.pizzaForm.sabores.length) {
        this.addPizzaFlavor();
      }

      this.$bvModal.show("modalCardapioPizza");
    },
    duplicatePizza(pizza) {
      const usedProductIds = new Set(
        this.pizzas
          .filter((item) => item.id !== pizza.id)
          .map((item) => item.cardapio_produto_id)
      );
      const targetProduct =
        this.publishedProducts.find(
          (product) =>
            product.id !== pizza.cardapio_produto_id &&
            !usedProductIds.has(product.id)
        ) || null;

      if (!targetProduct) {
        this.$toast.warning(
          "Publique outro produto no catálogo antes de duplicar a variante."
        );
        return;
      }

      this.pizzaForm = {
        id: null,
        cardapio_produto_id: targetProduct.id,
        nome_template: `Cópia de ${pizza.nome_template}`,
        regra_preco: pizza.regra_preco || "maior_sabor",
        max_sabores_padrao: pizza.max_sabores_padrao || 2,
        ativo: true,
        tamanhos: (pizza.tamanhos || []).map((size, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          nome: size.nome,
          slug: size.slug || this.slugify(size.nome),
          precoText: this.moneyTextFromCents(size.preco_base_centavos || 0),
          max_sabores: size.max_sabores || 2,
          fatias: size.fatias || null,
          ativo: size.ativo !== false,
          ordem: index,
        })),
        sabores: (pizza.sabores || []).map((flavor, index) => ({
          id: null,
          localKey: `${Date.now()}-${Math.random()}`,
          produto_id: null,
          nome: flavor.nome,
          slug: flavor.slug || this.slugify(flavor.nome),
          descricao: flavor.descricao,
          precoText: this.moneyTextFromCents(flavor.preco_centavos || 0),
          ativo: flavor.ativo !== false,
          disponivel: flavor.disponivel !== false,
          ordem: index,
          tamanhos: flavor.tamanhos || [],
        })),
      };

      this.$bvModal.show("modalCardapioPizza");
    },
    onProductImageSelected(event) {
      const [file] = event.target.files || [];

      if (!this.productForm || !file) {
        return;
      }

      if (this.productForm.imagePreviewUrl) {
        URL.revokeObjectURL(this.productForm.imagePreviewUrl);
      }

      this.productForm.imageFile = file;
      this.productForm.imagePreviewUrl = URL.createObjectURL(file);
    },
    clearVisualPreview(type) {
      const key = type === "capa" ? "capaPreviewUrl" : "logoPreviewUrl";

      if (this.visualForm[key]) {
        URL.revokeObjectURL(this.visualForm[key]);
        this.visualForm[key] = null;
      }
    },
    onVisualImageSelected(event, type) {
      const [file] = event.target.files || [];
      const fileKey = type === "capa" ? "capaFile" : "logoFile";
      const previewKey = type === "capa" ? "capaPreviewUrl" : "logoPreviewUrl";

      if (!file) {
        return;
      }

      this.clearVisualPreview(type);
      this.visualForm[fileKey] = file;
      this.visualForm[previewKey] = URL.createObjectURL(file);
    },
    normalizedHexColor(value, fallback) {
      const color = String(value || "").trim();

      if (/^#[0-9a-f]{6}$/i.test(color)) {
        return color;
      }

      return fallback;
    },
    hexToRgb(value) {
      const color = this.normalizedHexColor(value, "");

      if (!color) {
        return null;
      }

      const hex = color.replace("#", "");

      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    },
    relativeLuminance(value) {
      const rgb = this.hexToRgb(value);

      if (!rgb) {
        return null;
      }

      const channel = (number) => {
        const normalized = number / 255;

        return normalized <= 0.03928
          ? normalized / 12.92
          : Math.pow((normalized + 0.055) / 1.055, 2.4);
      };

      return (
        0.2126 * channel(rgb.r) +
        0.7152 * channel(rgb.g) +
        0.0722 * channel(rgb.b)
      );
    },
    colorContrast(colorA, colorB) {
      const luminanceA = this.relativeLuminance(colorA);
      const luminanceB = this.relativeLuminance(colorB);

      if (luminanceA === null || luminanceB === null) {
        return 0;
      }

      const light = Math.max(luminanceA, luminanceB);
      const dark = Math.min(luminanceA, luminanceB);

      return (light + 0.05) / (dark + 0.05);
    },
    addPizzaSize() {
      if (!this.pizzaForm) return;

      const index = this.pizzaForm.tamanhos.length;
      const nome = index === 0 ? "Grande" : `Tamanho ${index + 1}`;

      this.pizzaForm.tamanhos.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        nome,
        slug: this.slugify(nome),
        precoText: "R$ 0,00",
        max_sabores: this.pizzaForm.max_sabores_padrao || 2,
        fatias: null,
        ativo: true,
        ordem: index,
      });
    },
    removePizzaSize(index) {
      if (!this.pizzaForm) return;
      const removed = this.pizzaForm.tamanhos[index];
      this.pizzaForm.tamanhos.splice(index, 1);

      if (removed?.slug) {
        this.pizzaForm.sabores = this.pizzaForm.sabores.map((flavor) =>
          Object.assign({}, flavor, {
            tamanhos: (flavor.tamanhos || []).filter(
              (slug) => slug !== removed.slug
            ),
          })
        );
      }
    },
    addPizzaFlavor() {
      if (!this.pizzaForm) return;

      this.pizzaForm.sabores.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        produto_id: null,
        nome: "",
        slug: "",
        descricao: "",
        precoText: "R$ 0,00",
        ativo: true,
        disponivel: true,
        ordem: this.pizzaForm.sabores.length,
        tamanhos: [],
      });
    },
    removePizzaFlavor(index) {
      if (!this.pizzaForm) return;
      this.pizzaForm.sabores.splice(index, 1);
    },
    async savePizza() {
      if (!this.pizzaForm) return;

      this.clearFieldErrors();
      this.setSaving("pizza", true);
      try {
        const payload = Object.assign({}, this.pizzaForm, {
          tamanhos: this.pizzaForm.tamanhos.map((size, index) => ({
            id: size.id,
            nome: size.nome,
            slug: size.slug || this.slugify(size.nome),
            preco_base_centavos: this.parseMoneyToCents(size.precoText),
            max_sabores: size.max_sabores || 2,
            fatias: size.fatias || null,
            ativo: size.ativo !== false,
            ordem: index,
          })),
          sabores: this.pizzaForm.sabores.map((flavor, index) => ({
            id: flavor.id,
            produto_id: flavor.produto_id || null,
            nome: flavor.nome,
            slug: flavor.slug || this.slugify(flavor.nome),
            descricao: flavor.descricao,
            preco_centavos: this.parseMoneyToCents(flavor.precoText),
            ativo: flavor.ativo !== false,
            disponivel: flavor.disponivel !== false,
            ordem: index,
            tamanhos: flavor.tamanhos || [],
          })),
        });
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/pizzas",
          payload
        );
        const normalized = this.normalizePizza(this.unwrapResponse(data));
        const index = this.pizzas.findIndex(
          (item) => item.id === normalized.id
        );

        if (index >= 0) {
          this.pizzas.splice(index, 1, normalized);
        } else {
          this.pizzas.push(normalized);
        }

        const product = this.products.find(
          (item) => item.id === normalized.cardapio_produto_id
        );

        if (product) {
          product.type = "pizza";
          product.hasPizza = true;
        }

        this.$toast.success("Variante salva.");
        this.$bvModal.hide("modalCardapioPizza");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a variante.");
      } finally {
        this.setSaving("pizza", false);
      }
    },
    async togglePizza(pizza) {
      this.clearFieldErrors();
      this.setSaving("pizza-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/pizzas/${pizza.id}/status`,
          { ativo: !pizza.ativo }
        );
        const normalized = this.normalizePizza(this.unwrapResponse(data));
        const index = this.pizzas.findIndex(
          (item) => item.id === normalized.id
        );

        if (index >= 0) {
          this.pizzas.splice(index, 1, normalized);
        }

        this.$toast.success(
          normalized.ativo ? "Variante ativada." : "Variante pausada."
        );
      } catch (error) {
        this.showApiError(error, "Não foi possível alterar a variante.");
      } finally {
        this.setSaving("pizza-status", false);
      }
    },
    addOptionItem() {
      if (!this.optionGroupForm) return;

      this.optionGroupForm.opcoes.push({
        id: null,
        localKey: `${Date.now()}-${Math.random()}`,
        produto_id: null,
        nome: "",
        descricao: "",
        precoText: "R$ 0,00",
        ativo: true,
        disponivel: true,
        ordem: this.optionGroupForm.opcoes.length,
      });
    },
    removeOptionItem(index) {
      if (!this.optionGroupForm) return;
      this.optionGroupForm.opcoes.splice(index, 1);
    },
    async saveOptionGroup() {
      if (!this.optionGroupForm) return;

      this.clearFieldErrors();
      this.setSaving("option-group", true);
      try {
        const isGlobal = this.optionGroupForm.mode !== "produto";
        const regras =
          isGlobal && this.optionGroupForm.globalScope === "categorias"
            ? {
                escopo: "categorias",
                categorias: (this.optionGroupForm.categoria_ids || [])
                  .map((categoryId) => Number(categoryId))
                  .filter((categoryId) => categoryId > 0),
              }
            : null;
        const payload = Object.assign({}, this.optionGroupForm, {
          mode: undefined,
          globalScope: undefined,
          categoria_ids: undefined,
          cardapio_produto_id: isGlobal
            ? null
            : this.optionGroupForm.cardapio_produto_id,
          regras,
          maximo: this.optionGroupForm.maximo || null,
          opcoes: this.optionGroupForm.opcoes.map((option, index) => ({
            id: option.id,
            produto_id: option.produto_id || null,
            nome: option.nome,
            descricao: option.descricao,
            preco_adicional_centavos: this.parseMoneyToCents(option.precoText),
            ativo: option.ativo !== false,
            disponivel: option.disponivel !== false,
            ordem: index,
          })),
        });
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/opcoes",
          payload
        );
        const normalized = this.normalizeOptionGroup(this.unwrapResponse(data));
        const index = this.optionGroups.findIndex(
          (group) => group.id === normalized.id
        );

        if (index >= 0) {
          this.optionGroups.splice(index, 1, normalized);
        } else {
          this.optionGroups.push(normalized);
        }

        this.$toast.success("Grupo de opções salvo.");
        this.$bvModal.hide("modalCardapioOptionGroup");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o grupo.");
      } finally {
        this.setSaving("option-group", false);
      }
    },
    async toggleOptionGroup(group) {
      this.clearFieldErrors();
      this.setSaving("option-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/opcoes/${group.id}/status`,
          { ativo: !group.ativo }
        );
        const normalized = this.normalizeOptionGroup(this.unwrapResponse(data));
        const index = this.optionGroups.findIndex(
          (item) => item.id === normalized.id
        );

        if (index >= 0) {
          this.optionGroups.splice(index, 1, normalized);
        }

        this.$toast.success(
          normalized.ativo ? "Grupo ativado." : "Grupo pausado."
        );
      } catch (error) {
        this.showApiError(error, "Não foi possível alterar o status do grupo.");
      } finally {
        this.setSaving("option-status", false);
      }
    },
    async duplicateOptionGroup(group) {
      this.clearFieldErrors();
      this.setSaving("option-duplicate", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/opcoes/${group.id}/duplicar`
        );
        this.optionGroups.push(
          this.normalizeOptionGroup(this.unwrapResponse(data))
        );
        this.$toast.success("Grupo duplicado.");
      } catch (error) {
        this.showApiError(error, "Não foi possível duplicar o grupo.");
      } finally {
        this.setSaving("option-duplicate", false);
      }
    },
    pauseTargetConfig(type, target) {
      const configs = {
        category: {
          label: "Categoria",
          name: target?.name || "Categoria",
          savingKey: "category-status",
          url: `/restaurante/cardapio-online/categorias/${target?.id}/status`,
          payloadKey: "ativo",
          replace: (payload) =>
            this.replaceCategory(this.normalizeCategory(payload)),
          success: "Categoria pausada",
        },
        product: {
          label: "Produto",
          name: target?.name || "Produto",
          savingKey: "product-status",
          url: `/restaurante/cardapio-online/produtos/${target?.id}/status`,
          payloadKey: "disponivel",
          replace: (payload) =>
            this.replaceProduct(this.normalizeProduct(payload)),
          success: "Produto pausado",
        },
        option: {
          label: "Opção",
          name: target?.nome || "Opção",
          savingKey: "option-item-status",
          url: `/restaurante/cardapio-online/opcoes/itens/${target?.id}/status`,
          payloadKey: "disponivel",
          replace: (payload) =>
            this.replaceOptionGroup(this.normalizeOptionGroup(payload)),
          success: "Opção pausada",
        },
        cardapio: {
          label: "Cardápio inteiro",
          name: this.config.name || "Cardápio",
          savingKey: "config",
          success: "Cardápio pausado",
        },
      };

      return configs[type] || null;
    },
    openPauseModal(type, target) {
      const config = this.pauseTargetConfig(type, target);

      if (!config || !target?.id) {
        this.$toast.warning("Salve o item antes de pausar a venda.");
        return;
      }

      this.pauseForm = {
        type,
        target,
        typeLabel: config.label,
        name: config.name,
        mode: "manual",
        customUntil: "",
        savingKey: config.savingKey,
      };
      this.$bvModal.show("modalCardapioPause");
    },
    async confirmPauseTarget() {
      const config = this.pauseTargetConfig(
        this.pauseForm.type,
        this.pauseForm.target
      );

      if (!config) {
        return;
      }

      if (this.pauseForm.type === "cardapio") {
        const previousEnabled = this.config.habilitado;
        const previousMessage = this.config.closedMessage;

        this.config.habilitado = false;
        this.config.closedMessage =
          this.pauseForm.message || this.config.closedMessage;

        const saved = await this.saveConfiguration(
          `${config.success} ${this.pauseLabel(this.pauseForm.mode)}.`
        );

        if (saved) {
          this.$bvModal.hide("modalCardapioPause");
          return;
        }

        this.config.habilitado = previousEnabled;
        this.config.closedMessage = previousMessage;
        return;
      }

      const pausadoAte = this.pausePayload(
        this.pauseForm.mode,
        this.pauseForm.customUntil
      );

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving(config.savingKey, true);
      try {
        const payload = {
          [config.payloadKey]: false,
          pausado_ate: pausadoAte,
        };
        const { data } = await this.$http.post(config.url, payload);

        config.replace(this.unwrapResponse(data));
        this.$toast.success(
          `${config.success} ${this.pauseLabel(this.pauseForm.mode)}.`
        );
        this.$bvModal.hide("modalCardapioPause");
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a venda.");
      } finally {
        this.setSaving(config.savingKey, false);
      }
    },
    pausePayload(mode, customValue = null) {
      if (mode === "manual") {
        return null;
      }

      const date = new Date();

      if (mode === "hour") {
        date.setHours(date.getHours() + 1);
        return date.toISOString();
      }

      if (mode === "today") {
        date.setHours(23, 59, 59, 999);
        return date.toISOString();
      }

      if (mode === "custom") {
        const value = customValue;

        if (!value) {
          this.$toast.warning("Informe quando a venda deve voltar.");
          return false;
        }

        const parsed = new Date(value.replace(" ", "T"));

        if (Number.isNaN(parsed.getTime())) {
          this.$toast.warning("Data de pausa invalida.");
          return false;
        }

        return parsed.toISOString();
      }

      return null;
    },
    pauseLabel(mode) {
      if (mode === "hour") {
        return "por 1 hora";
      }

      if (mode === "today") {
        return "até o fim do dia";
      }

      if (mode === "custom") {
        return "até o horário escolhido";
      }

      return "ate ativar novamente";
    },
    replaceCategory(normalized) {
      const index = this.categories.findIndex(
        (item) => item.id === normalized.id || item.slug === normalized.slug
      );

      if (index >= 0) {
        this.categories.splice(index, 1, normalized);
      }
    },
    replaceProduct(normalized) {
      const index = this.products.findIndex(
        (item) =>
          item.id === normalized.id || item.productId === normalized.productId
      );

      if (index >= 0) {
        this.products.splice(index, 1, normalized);
      }
    },
    replaceOptionGroup(normalized) {
      const index = this.optionGroups.findIndex(
        (item) => item.id === normalized.id
      );

      if (index >= 0) {
        this.optionGroups.splice(index, 1, normalized);
      }
    },
    async pauseCategory(category, mode = "manual") {
      if (!category.id) {
        this.$toast.warning("Salve a categoria antes de pausar.");
        return;
      }

      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("category-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/categorias/${category.id}/status`,
          { ativo: false, pausado_ate: pausadoAte }
        );
        this.replaceCategory(this.normalizeCategory(this.unwrapResponse(data)));
        this.$toast.success(`Categoria pausada ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a categoria.");
      } finally {
        this.setSaving("category-status", false);
      }
    },
    async activateCategory(category) {
      this.clearFieldErrors();
      this.setSaving("category-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/categorias/${category.id}/status`,
          { ativo: true }
        );
        this.replaceCategory(this.normalizeCategory(this.unwrapResponse(data)));
        this.$toast.success("Categoria ativada.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar a categoria.");
      } finally {
        this.setSaving("category-status", false);
      }
    },
    async pauseOptionItem(option, mode = "manual") {
      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("option-item-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/opcoes/itens/${option.id}/status`,
          { disponivel: false, pausado_ate: pausadoAte }
        );
        this.replaceOptionGroup(
          this.normalizeOptionGroup(this.unwrapResponse(data))
        );
        this.$toast.success(`Opção pausada ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar a opção.");
      } finally {
        this.setSaving("option-item-status", false);
      }
    },
    async activateOptionItem(option) {
      this.clearFieldErrors();
      this.setSaving("option-item-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/opcoes/itens/${option.id}/status`,
          { disponivel: true }
        );
        this.replaceOptionGroup(
          this.normalizeOptionGroup(this.unwrapResponse(data))
        );
        this.$toast.success("Opção ativada.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar a opção.");
      } finally {
        this.setSaving("option-item-status", false);
      }
    },
    async pauseProduct(product, mode = "manual") {
      if (!product.id) {
        this.$toast.warning("Publique o produto no site antes de pausar.");
        return;
      }

      const pausadoAte = this.pausePayload(mode);

      if (pausadoAte === false) {
        return;
      }

      this.clearFieldErrors();
      this.setSaving("product-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/produtos/${product.id}/status`,
          { disponivel: false, pausado_ate: pausadoAte }
        );
        const normalized = this.normalizeProduct(this.unwrapResponse(data));
        this.replaceProduct(normalized);
        this.$toast.success(`Produto pausado ${this.pauseLabel(mode)}.`);
      } catch (error) {
        this.showApiError(error, "Não foi possível pausar o produto.");
      } finally {
        this.setSaving("product-status", false);
      }
    },
    async activateProduct(product) {
      if (!product.id) {
        this.$toast.warning("Publique o produto no site antes de ativar.");
        return;
      }

      this.clearFieldErrors();
      this.setSaving("product-status", true);
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/produtos/${product.id}/status`,
          { disponivel: true }
        );
        this.replaceProduct(this.normalizeProduct(this.unwrapResponse(data)));
        this.$toast.success("Produto ativado.");
      } catch (error) {
        this.showApiError(error, "Não foi possível ativar o produto.");
      } finally {
        this.setSaving("product-status", false);
      }
    },
    async savePublishing() {
      this.config.name = this.publishingForm.name;
      this.config.slug = this.slugify(this.publishingForm.slug);
      this.config.description = this.publishingForm.description;
      this.config.scheduleLabel = this.publishingForm.scheduleLabel;
      this.config.closedMessage = this.publishingForm.closedMessage;
      this.config.publicUrl = `http://localhost:5174/${this.config.slug}`;

      const saved = await this.saveConfiguration("Publicação salva.");

      if (saved) {
        this.$bvModal.hide("modalCardapioPublishing");
      }
    },
    async saveDelivery() {
      this.config.deliveryFeeCents = this.parseMoneyToCents(
        this.deliveryForm.feeText
      );
      this.config.minimumOrderCents = this.parseMoneyToCents(
        this.deliveryForm.minimumText
      );
      this.config.minMinutes = this.deliveryForm.minMinutes;
      this.config.maxMinutes = this.deliveryForm.maxMinutes;
      this.config.deliveryEnabled = Boolean(this.deliveryForm.deliveryEnabled);
      this.config.useGoogleMaps = Boolean(this.deliveryForm.useGoogleMaps);
      this.config.baseAddress = this.deliveryForm.baseAddress;
      this.config.baseLatitude = this.deliveryForm.baseLatitude;
      this.config.baseLongitude = this.deliveryForm.baseLongitude;
      this.config.googleMapsBrowserKey = this.deliveryForm.googleMapsBrowserKey;
      this.config.googleMapsMapId = this.deliveryForm.googleMapsMapId;

      this.clearFieldErrors();
      this.setSaving("delivery", true);
      try {
        const deliveryPayload = {
          aceita_entrega: this.config.deliveryEnabled,
          taxa_entrega_padrao_centavos: this.config.deliveryFeeCents,
          pedido_minimo_centavos: this.config.minimumOrderCents,
          usar_google_maps_entrega: this.config.useGoogleMaps,
          endereco_base: this.config.baseAddress,
          latitude_base: this.config.baseLatitude || null,
          longitude_base: this.config.baseLongitude || null,
          google_maps_browser_key: this.config.googleMapsBrowserKey || null,
          google_maps_map_id: this.config.googleMapsMapId || null,
          tempo_entrega_minutos_min: this.config.minMinutes,
          tempo_entrega_minutos_max: this.config.maxMinutes,
        };

        if (this.deliveryForm.googleMapsApiKey) {
          deliveryPayload.google_maps_api_key =
            this.deliveryForm.googleMapsApiKey;
        }

        if (this.config.useGoogleMaps) {
          deliveryPayload.area = {
            nome: this.deliveryForm.areaName,
            raio_km_min: this.deliveryForm.radiusMinKm,
            raio_km_max: this.deliveryForm.radiusMaxKm,
            bloqueada: Boolean(this.deliveryForm.blockedArea),
            taxa_entrega_centavos: this.config.deliveryFeeCents,
            frete_gratis_acima_centavos: this.parseMoneyToCents(
              this.deliveryForm.freeAboveText
            ),
            ativo: true,
          };
        }

        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/entrega",
          deliveryPayload
        );
        const payload = this.unwrapResponse(data);

        this.areasEntrega = payload.areas_entrega || this.areasEntrega;
        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            configuracao: payload.configuracao || {},
            areas_entrega: this.areasEntrega,
          })
        );
        this.$toast.success("Entrega salva.");
        this.$bvModal.hide("modalCardapioDelivery");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar entrega.");
      } finally {
        this.setSaving("delivery", false);
      }
    },
    async savePickup() {
      this.config.pickupEnabled = Boolean(this.deliveryForm.pickupEnabled);

      this.clearFieldErrors();
      this.setSaving("pickup", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/retirada",
          {
            aceita_retirada: this.config.pickupEnabled,
            tempo_preparo_minutos: this.deliveryForm.pickupMinutes,
            mensagem: this.deliveryForm.pickupMessage,
          }
        );
        const config = this.unwrapResponse(data);

        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            configuracao: config,
          })
        );
        this.$toast.success("Retirada salva.");
        this.$bvModal.hide("modalCardapioPickup");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar retirada.");
      } finally {
        this.setSaving("pickup", false);
      }
    },
    async saveDeliveryCost() {
      this.clearFieldErrors();
      this.setSaving("delivery-cost", true);
      try {
        const deliveryCostPayload = {
          aceita_entrega: this.deliveryForm.deliveryEnabled,
          taxa_entrega_padrao_centavos: this.parseMoneyToCents(
            this.deliveryForm.feeText
          ),
          pedido_minimo_centavos: this.parseMoneyToCents(
            this.deliveryForm.minimumText
          ),
          tempo_entrega_minutos_min: this.deliveryForm.minMinutes,
          tempo_entrega_minutos_max: this.deliveryForm.maxMinutes,
          usar_google_maps_entrega: Boolean(this.deliveryForm.useGoogleMaps),
          endereco_base: this.deliveryForm.baseAddress,
          latitude_base: this.deliveryForm.baseLatitude || null,
          longitude_base: this.deliveryForm.baseLongitude || null,
          google_maps_browser_key:
            this.deliveryForm.googleMapsBrowserKey || null,
          google_maps_map_id: this.deliveryForm.googleMapsMapId || null,
          area: {
            nome: this.deliveryForm.areaName,
            raio_km_min: this.deliveryForm.radiusMinKm,
            raio_km_max: this.deliveryForm.radiusMaxKm,
            bloqueada: Boolean(this.deliveryForm.blockedArea),
            taxa_entrega_centavos: this.parseMoneyToCents(
              this.deliveryForm.feeText
            ),
            frete_gratis_acima_centavos: this.parseMoneyToCents(
              this.deliveryForm.freeAboveText
            ),
            ativo: true,
          },
        };

        if (this.deliveryForm.googleMapsApiKey) {
          deliveryCostPayload.google_maps_api_key =
            this.deliveryForm.googleMapsApiKey;
        }

        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/entrega",
          deliveryCostPayload
        );
        const payload = this.unwrapResponse(data);

        this.areasEntrega = payload.areas_entrega || this.areasEntrega;
        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            configuracao: payload.configuracao || {},
            areas_entrega: this.areasEntrega,
          })
        );
        this.$toast.success("Regra de entrega salva.");
        this.$bvModal.hide("modalCardapioDeliveryCost");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a regra.");
      } finally {
        this.setSaving("delivery-cost", false);
      }
    },
    zoneScopeLabel(zone) {
      if (zone.bairro) {
        return `Bairro: ${zone.bairro}`;
      }

      if (zone.cep_inicio && zone.cep_fim) {
        return `CEP ${zone.cep_inicio} a ${zone.cep_fim}`;
      }

      if (zone.cep_inicio) {
        return `CEP ${zone.cep_inicio}`;
      }

      return "Sem bairro/CEP definido";
    },
    newZone() {
      this.zoneForm = {
        open: true,
        id: null,
        nome: "Zona de entrega",
        bairro: "",
        cepInicio: "",
        cepFim: "",
        feeText: this.deliveryForm.feeText || "R$ 0,00",
        minimumText: "R$ 0,00",
        freeAboveText: "R$ 0,00",
        minMinutes: this.deliveryForm.minMinutes,
        maxMinutes: this.deliveryForm.maxMinutes,
        bloqueada: false,
      };
    },
    editZone(zone) {
      this.zoneForm = {
        open: true,
        id: zone.id,
        nome: zone.nome || "Zona de entrega",
        bairro: zone.bairro || "",
        cepInicio: zone.cep_inicio || "",
        cepFim: zone.cep_fim || "",
        feeText: this.moneyTextFromCents(zone.taxa_entrega_centavos || 0),
        minimumText: this.moneyTextFromCents(zone.pedido_minimo_centavos || 0),
        freeAboveText: this.moneyTextFromCents(
          zone.frete_gratis_acima_centavos || 0
        ),
        minMinutes: zone.tempo_entrega_minutos_min,
        maxMinutes: zone.tempo_entrega_minutos_max,
        bloqueada: Boolean(zone.bloqueada),
      };
    },
    cancelZone() {
      this.zoneForm.open = false;
    },
    async saveZone() {
      const freeAboveCents = this.parseMoneyToCents(this.zoneForm.freeAboveText);
      const payload = {
        id: this.zoneForm.id || undefined,
        nome: this.zoneForm.nome || "Zona de entrega",
        bairro: this.zoneForm.bairro || null,
        cep_inicio: this.zoneForm.cepInicio || null,
        cep_fim: this.zoneForm.cepFim || null,
        taxa_entrega_centavos: this.parseMoneyToCents(this.zoneForm.feeText),
        pedido_minimo_centavos: this.parseMoneyToCents(
          this.zoneForm.minimumText
        ),
        frete_gratis_acima_centavos: freeAboveCents > 0 ? freeAboveCents : null,
        tempo_entrega_minutos_min: this.zoneForm.minMinutes || null,
        tempo_entrega_minutos_max: this.zoneForm.maxMinutes || null,
        bloqueada: Boolean(this.zoneForm.bloqueada),
        ativo: true,
      };

      this.clearFieldErrors();
      this.setSaving("delivery-zone", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/entrega/zonas",
          payload
        );
        const result = this.unwrapResponse(data);

        this.areasEntrega = result.areas_entrega || this.areasEntrega;
        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            areas_entrega: this.areasEntrega,
          })
        );
        this.zoneForm.open = false;
        this.$toast.success("Zona de entrega salva.");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a zona.");
      } finally {
        this.setSaving("delivery-zone", false);
      }
    },
    async toggleZone(zone) {
      try {
        const { data } = await this.$http.post(
          `/restaurante/cardapio-online/entrega/zonas/${zone.id}/status`,
          { ativo: !zone.ativo }
        );
        const result = this.unwrapResponse(data);

        this.areasEntrega = result.areas_entrega || this.areasEntrega;
        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            areas_entrega: this.areasEntrega,
          })
        );
      } catch (error) {
        this.showApiError(error, "Não foi possível atualizar a zona.");
      }
    },
    async deleteZone(zone) {
      const confirmacao = await this.$bvModal.msgBoxConfirm(
        `Remover a zona "${zone.nome}"?`,
        {
          okTitle: "Remover",
          cancelTitle: "Cancelar",
          okVariant: "danger",
          centered: true,
        }
      );

      if (!confirmacao) {
        return;
      }

      try {
        const { data } = await this.$http.delete(
          `/restaurante/cardapio-online/entrega/zonas/${zone.id}`
        );
        const result = this.unwrapResponse(data);

        this.areasEntrega = result.areas_entrega || this.areasEntrega;
        this.applyManagementData(
          Object.assign({}, this.snapshotManagementData(), {
            areas_entrega: this.areasEntrega,
          })
        );

        if (this.zoneForm.id === zone.id) {
          this.zoneForm.open = false;
        }

        this.$toast.success("Zona de entrega removida.");
      } catch (error) {
        this.showApiError(error, "Não foi possível remover a zona.");
      }
    },
    buildProductPayload() {
      return {
        id: this.productForm.id,
        produto_id: this.productForm.productId,
        categoria_id: this.productForm.categoryId,
        tipo: this.productForm.type || "produto",
        nome_publico: this.productForm.name,
        slug: this.productForm.slug,
        descricao_publica: this.productForm.description,
        preco_centavos: this.parseMoneyToCents(this.productForm.priceText),
        imagem_principal_url: this.productForm.imageUrl,
        ativo: true,
        publicado: true,
        destaque: Boolean(this.productForm.highlighted),
        disponivel: Boolean(this.productForm.available),
      };
    },
    productPayloadForRequest() {
      const payload = this.buildProductPayload();

      if (!this.productForm.imageFile) {
        return payload;
      }

      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const value = payload[key];

        if (value === null || value === undefined) {
          return;
        }

        if (typeof value === "boolean") {
          formData.append(key, value ? "1" : "0");
          return;
        }

        formData.append(key, value);
      });

      formData.append("imagem", this.productForm.imageFile);

      return formData;
    },
    async saveProduct() {
      if (!this.productForm) return;

      this.clearFieldErrors();
      this.setSaving("product", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/produtos",
          this.productPayloadForRequest()
        );
        const normalized = this.normalizeProduct(this.unwrapResponse(data));
        const index = this.products.findIndex(
          (product) =>
            product.id === normalized.id ||
            product.productId === normalized.productId
        );

        if (index >= 0) {
          this.products.splice(index, 1, normalized);
        } else {
          this.products.push(normalized);
        }

        this.$toast.success("Produto salvo no site.");
        this.$bvModal.hide("modalCardapioProduct");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o produto.");
      } finally {
        this.setSaving("product", false);
      }
    },
    async saveVisual() {
      this.clearFieldErrors();
      this.setSaving("visual", true);
      try {
        const formData = new FormData();

        formData.append("logo_url", this.visualForm.logoUrl || "");
        formData.append("capa_url", this.visualForm.capaUrl || "");
        formData.append("primary_color", this.visualForm.primaryColor || "");
        formData.append("accent_color", this.visualForm.accentColor || "");
        formData.append(
          "background_color",
          this.visualForm.backgroundColor || ""
        );
        formData.append("card_style", this.visualForm.cardStyle || "soft");
        formData.append(
          "button_style",
          this.visualForm.buttonStyle || "rounded"
        );
        formData.append("empty_text", this.visualForm.emptyText || "");
        formData.append("error_text", this.visualForm.errorText || "");
        formData.append("closed_text", this.visualForm.closedText || "");
        formData.append("checkout_text", this.visualForm.checkoutText || "");
        formData.append("tracking_text", this.visualForm.trackingText || "");

        if (this.visualForm.logoFile) {
          formData.append("logo", this.visualForm.logoFile);
        }

        if (this.visualForm.capaFile) {
          formData.append("capa", this.visualForm.capaFile);
        }

        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/visual-textos",
          formData
        );
        const payload = this.unwrapResponse(data);

        this.applyVisualData(payload.visual || {}, payload.textos || []);
        this.textosPublicos = payload.textos || this.textosPublicos;
        this.$toast.success("Visual e textos salvos.");
        this.$bvModal.hide("modalCardapioVisual");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar visual e textos.");
      } finally {
        this.setSaving("visual", false);
      }
    },
    async publishCardapio() {
      if (!this.canPublishCardapio) {
        this.$toast.warning(
          "Antes de publicar, deixe pelo menos um produto disponível e ative entrega ou retirada."
        );
        return;
      }

      const previousEnabled = this.config.habilitado;
      this.config.habilitado = true;
      const saved = await this.saveConfiguration("Cardápio publicado.");

      if (!saved) {
        this.config.habilitado = previousEnabled;
      }
    },
    async pauseCardapio() {
      this.openCardapioPauseModal();
    },
    promotionTargetLabel(promotion) {
      if (promotion.cardapio_produto_id) {
        return this.productName(promotion.cardapio_produto_id);
      }

      if (promotion.categoria_id) {
        return this.categoryName(promotion.categoria_id);
      }

      return "Todo o cardápio";
    },
    promotionPeriodLabel(promotion) {
      if (promotion.inicio_at && promotion.fim_at) {
        return `${promotion.inicio_at} até ${promotion.fim_at}`;
      }

      if (promotion.fim_at) {
        return `Até ${promotion.fim_at}`;
      }

      return "Sem data final";
    },
    openPromotionModal(promotion = null) {
      const regras = promotion?.regras || {};
      const targetType = promotion?.categoria_id
        ? "categoria"
        : promotion?.cardapio_produto_id
        ? "produto"
        : "cardapio";

      this.promotionForm = {
        id: promotion?.id || null,
        preset: regras.preset || "banner",
        name: promotion?.nome || "Promoção do cardápio",
        subtitle: regras.subtitulo || "",
        description: promotion?.descricao || "",
        targetType,
        productId:
          promotion?.cardapio_produto_id ||
          this.publishedProducts[0]?.id ||
          null,
        categoryId: promotion?.categoria_id || this.categories[0]?.id || null,
        discountType:
          promotion?.tipo === "percentual" || promotion?.percentual_desconto > 0
            ? "percentual"
            : "valor",
        discountText: this.moneyTextFromCents(
          promotion?.valor_desconto_centavos || 0
        ),
        discountPercent: Number(promotion?.percentual_desconto || 0),
        previousPriceText: this.moneyTextFromCents(
          regras.preco_de_centavos || 0
        ),
        promotionalPriceText: this.moneyTextFromCents(
          regras.preco_por_centavos || 0
        ),
        bannerText: regras.banner_text || promotion?.descricao || "",
        giftText: regras.brinde || "",
        imageUrl: promotion?.imagem_url || regras.imagem_url || "",
        imageFile: null,
        imagePreviewUrl: null,
        start: promotion?.inicio_at || "",
        end: promotion?.fim_at || "",
        weekdays: Array.isArray(regras.dias_semana) ? regras.dias_semana : [],
        enabled: promotion ? Boolean(promotion.ativo) : true,
      };
      this.$bvModal.show("modalCardapioPromotions");
    },
    applyPromotionPreset(preset) {
      this.promotionForm.preset = preset;

      const presetData = {
        banner: {
          name: "Banner promocional",
          subtitle: "Oferta por tempo limitado",
          discountType: "valor",
        },
        preco_especial: {
          name: "Preço especial",
          subtitle: "Preço promocional no cardápio",
          discountType: "valor",
        },
        combo: {
          name: "Combo especial",
          subtitle: "Itens juntos por um preço melhor",
          discountType: "valor",
        },
        compre_ganhe: {
          name: "Compre e ganhe",
          subtitle: "Benefício na compra deste item",
          discountType: "valor",
        },
        dia_semana: {
          name: "Promoção da semana",
          subtitle: "Válida nos dias selecionados",
          discountType: "valor",
        },
        cupom: {
          name: "Cupom de desconto",
          subtitle: "Use o código no checkout",
          discountType: "percentual",
        },
      }[preset];

      if (!presetData) {
        return;
      }

      this.promotionForm.name = presetData.name;
      this.promotionForm.subtitle = presetData.subtitle;
      this.promotionForm.discountType = presetData.discountType;

      if (preset === "dia_semana" && !this.promotionForm.weekdays.length) {
        this.promotionForm.weekdays = [2];
      }
    },
    onPromotionImageSelected(event) {
      const file = event.target.files && event.target.files[0];

      if (!file) {
        return;
      }

      if (this.promotionForm.imagePreviewUrl) {
        URL.revokeObjectURL(this.promotionForm.imagePreviewUrl);
      }

      this.promotionForm.imageFile = file;
      this.promotionForm.imagePreviewUrl = URL.createObjectURL(file);
    },
    openCouponModal(coupon = null) {
      this.couponForm = {
        id: coupon?.id || null,
        code: coupon?.codigo || "",
        name: coupon?.nome || "",
        discountType:
          coupon?.tipo_desconto === "percentual" ||
          coupon?.percentual_desconto > 0
            ? "percentual"
            : "valor",
        discountText: this.moneyTextFromCents(
          coupon?.valor_desconto_centavos || 0
        ),
        discountPercent: Number(coupon?.percentual_desconto || 0),
        minimumText: this.moneyTextFromCents(
          coupon?.pedido_minimo_centavos || 0
        ),
        totalLimit: coupon?.limite_uso_total || null,
        start: coupon?.inicio_at || "",
        end: coupon?.fim_at || "",
        enabled: coupon ? Boolean(coupon.ativo) : true,
      };
      this.$bvModal.show("modalCardapioCoupon");
    },
    openComboModal(combo = null) {
      const itens = Array.isArray(combo?.itens) ? combo.itens : [];

      this.comboForm = {
        id: combo?.id || null,
        productId:
          combo?.cardapio_produto_id || this.publishedProducts[0]?.id || null,
        name: combo?.nome || "Combo",
        description: combo?.descricao || "",
        priceText: this.moneyTextFromCents(combo?.preco_combo_centavos || 0),
        itemsText: itens
          .map((item) => (typeof item === "string" ? item : item.nome || ""))
          .filter(Boolean)
          .join("\n"),
        enabled: combo ? Boolean(combo.ativo) : true,
      };
      this.$bvModal.show("modalCardapioCombo");
    },
    async savePromotion() {
      this.clearFieldErrors();
      this.setSaving("promotion", true);
      try {
        const isPercentual = this.promotionForm.discountType === "percentual";
        const product = this.products.find(
          (item) => item.id === this.promotionForm.productId
        );
        const promotionalPrice = this.parseMoneyToCents(
          this.promotionForm.promotionalPriceText
        );
        const calculatedDiscount =
          !isPercentual && promotionalPrice > 0 && product
            ? Math.max(0, product.priceCents - promotionalPrice)
            : this.parseMoneyToCents(this.promotionForm.discountText);
        const formData = new FormData();

        if (this.promotionForm.id) {
          formData.append("id", this.promotionForm.id);
        }

        formData.append("nome", this.promotionForm.name);
        formData.append("tipo", isPercentual ? "percentual" : "valor");
        formData.append(
          "descricao",
          this.promotionForm.description ||
            this.promotionForm.bannerText ||
            this.promotionForm.name
        );
        formData.append(
          "cardapio_produto_id",
          this.promotionForm.targetType === "produto"
            ? this.promotionForm.productId || ""
            : ""
        );
        formData.append(
          "categoria_id",
          this.promotionForm.targetType === "categoria"
            ? this.promotionForm.categoryId || ""
            : ""
        );
        formData.append(
          "valor_desconto_centavos",
          isPercentual ? 0 : calculatedDiscount
        );
        formData.append(
          "percentual_desconto",
          isPercentual ? Number(this.promotionForm.discountPercent || 0) : 0
        );
        formData.append("inicio_at", this.promotionForm.start || "");
        formData.append("fim_at", this.promotionForm.end || "");
        formData.append("ativo", this.promotionForm.enabled ? "1" : "0");
        formData.append("destaque", "1");
        formData.append("regras[preset]", this.promotionForm.preset);
        formData.append("regras[subtitulo]", this.promotionForm.subtitle || "");
        formData.append(
          "regras[banner_text]",
          this.promotionForm.bannerText || ""
        );
        formData.append("regras[brinde]", this.promotionForm.giftText || "");
        formData.append(
          "regras[preco_de_centavos]",
          this.parseMoneyToCents(this.promotionForm.previousPriceText)
        );
        formData.append("regras[preco_por_centavos]", promotionalPrice);
        (this.promotionForm.weekdays || []).forEach((day, index) => {
          formData.append(`regras[dias_semana][${index}]`, day);
        });

        if (this.promotionForm.imageUrl) {
          formData.append("imagem_url", this.promotionForm.imageUrl);
        }

        if (this.promotionForm.imageFile) {
          formData.append("imagem", this.promotionForm.imageFile);
        }

        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/promocoes",
          formData
        );
        const saved = this.unwrapResponse(data);
        const index = this.promocoes.findIndex((item) => item.id === saved.id);

        if (index >= 0) {
          this.promocoes.splice(index, 1, saved);
        } else {
          this.promocoes.push(saved);
        }

        this.$toast.success("Promoção salva.");
        this.$bvModal.hide("modalCardapioPromotions");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a promoção.");
      } finally {
        this.setSaving("promotion", false);
      }
    },
    async saveCoupon() {
      this.clearFieldErrors();
      this.setSaving("coupon", true);
      try {
        const isPercentual = this.couponForm.discountType === "percentual";
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/cupons",
          {
            id: this.couponForm.id || null,
            codigo: this.couponForm.code,
            nome: this.couponForm.name,
            tipo_desconto: isPercentual ? "percentual" : "valor",
            valor_desconto_centavos: isPercentual
              ? 0
              : this.parseMoneyToCents(this.couponForm.discountText),
            percentual_desconto: isPercentual
              ? Number(this.couponForm.discountPercent || 0)
              : 0,
            pedido_minimo_centavos: this.parseMoneyToCents(
              this.couponForm.minimumText
            ),
            limite_uso_total: this.couponForm.totalLimit || null,
            inicio_at: this.couponForm.start || null,
            fim_at: this.couponForm.end || null,
            ativo: Boolean(this.couponForm.enabled),
          }
        );
        const saved = this.unwrapResponse(data);
        const index = this.cupons.findIndex((item) => item.id === saved.id);

        if (index >= 0) {
          this.cupons.splice(index, 1, saved);
        } else {
          this.cupons.push(saved);
        }

        this.$toast.success("Cupom salvo.");
        this.$bvModal.hide("modalCardapioCoupon");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o cupom.");
      } finally {
        this.setSaving("coupon", false);
      }
    },
    async saveCombo() {
      this.clearFieldErrors();
      this.setSaving("combo", true);
      try {
        const itens = this.comboForm.itemsText
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean)
          .map((nome, index) => ({ nome, ordem: index }));
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/combos",
          {
            id: this.comboForm.id || null,
            cardapio_produto_id: this.comboForm.productId,
            nome: this.comboForm.name,
            descricao: this.comboForm.description,
            preco_combo_centavos: this.parseMoneyToCents(
              this.comboForm.priceText
            ),
            ativo: Boolean(this.comboForm.enabled),
            itens,
          }
        );
        const saved = this.unwrapResponse(data);
        const index = this.combos.findIndex((item) => item.id === saved.id);

        if (index >= 0) {
          this.combos.splice(index, 1, saved);
        } else {
          this.combos.push(saved);
        }

        this.$toast.success("Combo salvo.");
        this.$bvModal.hide("modalCardapioCombo");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o combo.");
      } finally {
        this.setSaving("combo", false);
      }
    },
    async saveCheckout() {
      this.clearFieldErrors();
      this.setSaving("checkout", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/pagamento",
          {
            pix: this.checkoutForm.pix,
            dinheiro: this.checkoutForm.cash,
            cartao: this.checkoutForm.card,
            mensagem: this.checkoutForm.message,
          }
        );

        this.applyOperationData(this.unwrapResponse(data));
        this.$toast.success("Checkout salvo.");
        this.$bvModal.hide("modalCardapioCheckout");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar o checkout.");
      } finally {
        this.setSaving("checkout", false);
      }
    },
    async saveOperation() {
      this.clearFieldErrors();
      this.setSaving("operation", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/operacao",
          {
            aceite_manual: this.operationForm.manualAccept,
            impressao_automatica: this.operationForm.printOrder,
            enviar_para_producao: true,
            mensagem: this.operationForm.note,
            regras: {
              sound: this.operationForm.sound,
            },
          }
        );

        this.applyOperationData(this.unwrapResponse(data));
        this.$toast.success("Operação salva.");
        this.$bvModal.hide("modalCardapioOperation");
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a operação.");
      } finally {
        this.setSaving("operation", false);
      }
    },
    async saveConfiguration(successMessage) {
      this.clearFieldErrors();
      this.setSaving("config", true);
      try {
        const { data } = await this.$http.post(
          "/restaurante/cardapio-online/configuracao",
          {
            slug: this.config.slug,
            nome_publico: this.config.name,
            descricao: this.config.description,
            habilitado: this.config.habilitado,
            aceita_entrega: this.config.deliveryEnabled,
            aceita_retirada: this.config.pickupEnabled,
            pedido_minimo_centavos: this.config.minimumOrderCents,
            taxa_entrega_padrao_centavos: this.config.deliveryFeeCents,
            tempo_entrega_minutos_min: this.config.minMinutes,
            tempo_entrega_minutos_max: this.config.maxMinutes,
            horarios: {
              resumo: this.config.scheduleLabel,
            },
            mensagem_fechado: this.config.closedMessage,
          }
        );
        const config = this.unwrapResponse(data);

        this.applyManagementData({
          configuracao: config,
          categorias: this.categories.map((category) => ({
            id: category.id,
            codigo: category.slug,
            nome: category.name,
            quantidade_produtos: category.count,
          })),
          produtos: this.products.map((product) => ({
            id: product.id,
            codigo: product.code,
            tipo: product.type,
            slug: product.slug,
            nome: product.name,
            descricao: product.description,
            categoria_id: product.categoryId,
            categoria_nome: product.categoryName,
            preco_centavos: product.priceCents,
            imagem_url: product.imageUrl,
            disponivel: product.available,
            sem_preparo: product.noPreparation,
            tem_pizza: product.hasPizza,
          })),
          opcoes: this.optionGroups,
          pizzas: this.pizzas,
          areas_entrega: this.areasEntrega,
          promocoes: this.promocoes,
          cupons: this.cupons,
          combos: this.combos,
          textos: this.textosPublicos,
        });
        this.$toast.success(successMessage);
        return true;
      } catch (error) {
        this.showApiError(error, "Não foi possível salvar a configuração.");
        return false;
      } finally {
        this.setSaving("config", false);
      }
    },
    snapshotManagementData() {
      return {
        configuracao: {
          slug: this.config.slug,
          nome_publico: this.config.name,
          descricao: this.config.description,
          habilitado: this.config.habilitado,
          aceita_entrega: this.config.deliveryEnabled,
          aceita_retirada: this.config.pickupEnabled,
          pedido_minimo_centavos: this.config.minimumOrderCents,
          taxa_entrega_padrao_centavos: this.config.deliveryFeeCents,
          tempo_entrega_minutos_min: this.config.minMinutes,
          tempo_entrega_minutos_max: this.config.maxMinutes,
          horario_resumo: this.config.scheduleLabel,
          mensagem_fechado: this.config.closedMessage,
          public_url: this.config.publicUrl,
          logo_url: this.visualForm.logoUrl,
          capa_url: this.visualForm.capaUrl,
        },
        categorias: this.categories.map((category) => ({
          id: category.id,
          codigo: category.slug,
          nome: category.name,
          quantidade_produtos: category.count,
        })),
        produtos: this.products.map((product) => ({
          id: product.id,
          produto_id: product.productId,
          codigo: product.code,
          tipo: product.type,
          slug: product.slug,
          nome: product.name,
          descricao: product.description,
          categoria_id: product.categoryId,
          categoria_nome: product.categoryName,
          preco_centavos: product.priceCents,
          imagem_url: product.imageUrl,
          disponivel: product.available,
          sem_preparo: product.noPreparation,
          tem_pizza: product.hasPizza,
        })),
        opcoes: this.optionGroups,
        pizzas: this.pizzas,
        areas_entrega: this.areasEntrega,
        promocoes: this.promocoes,
        cupons: this.cupons,
        combos: this.combos,
        textos: this.textosPublicos,
        operacao: {
          aceite_manual: this.operationForm.manualAccept,
          impressao_automatica: this.operationForm.printOrder,
          enviar_para_producao: true,
          formas_pagamento: {
            pix: this.checkoutForm.pix,
            dinheiro: this.checkoutForm.cash,
            cartao: this.checkoutForm.card,
          },
          mensagens_cliente: {
            checkout: this.checkoutForm.message,
            operacao: this.operationForm.note,
          },
          regras: {
            sound: this.operationForm.sound,
          },
        },
        visual: {
          logo_url: this.visualForm.logoUrl,
          capa_url: this.visualForm.capaUrl,
          primary_color: this.visualForm.primaryColor,
          accent_color: this.visualForm.accentColor,
          background_color: this.visualForm.backgroundColor,
          card_style: this.visualForm.cardStyle,
          button_style: this.visualForm.buttonStyle,
        },
      };
    },
    slugify(value) {
      return String(value || "restaurante-demo")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },
    initials(value) {
      return String(value || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
  },
};
</script>

<style scoped>
.overview-grid,
.content-grid {
  display: grid;
  gap: 16px;
}

.overview-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.presentation-alert {
  display: flex;
  gap: 16px;
  padding: 18px 22px;
  border: 1px solid #d8f3dc;
  border-radius: 6px;
  background: #f0fff4;
  color: #172c30;
}

.presentation-alert-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 6px;
  background: #16a34a;
  color: #ffffff;
}

.presentation-alert strong,
.presentation-alert p {
  margin: 0;
}

.presentation-alert p {
  color: #5f6678;
}

.overview-card,
.control-card {
  min-width: 0;
  padding: 18px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.overview-card span,
.overview-card small,
.control-card p,
.control-card li,
.control-card small,
.section-button span,
.section-header p,
.toolbar-line p {
  color: #7e8299;
}

.overview-card strong {
  display: block;
  margin: 8px 0 4px;
  color: #181c32;
  font-size: 24px;
  font-weight: 800;
}

.admin-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 20px;
}

.admin-sidebar {
  display: grid;
  align-content: start;
  gap: 10px;
}

.section-button {
  width: 100%;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.section-button strong,
.section-button span {
  display: block;
}

.section-button-active {
  border-color: #f3695f;
  background: #fff5f3;
}

.section-button-active strong {
  color: #f3695f;
}

.admin-content {
  min-width: 0;
}

.section-header,
.toolbar-line,
.control-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.section-header {
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ebedf3;
}

.section-header h4,
.control-card h5,
.toolbar-line h5 {
  margin: 0 0 6px;
  color: #181c32;
  font-weight: 800;
}

.content-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.control-card-wide {
  grid-column: span 2;
}

.brand-preview {
  display: flex;
  align-items: center;
  gap: 18px;
}

.brand-preview img {
  width: 78px;
  height: 78px;
  object-fit: contain;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.brand-preview strong {
  display: block;
  color: #f3695f;
  word-break: break-word;
}

.visual-editor {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 310px;
  gap: 18px;
  align-items: start;
}

.visual-upload-card {
  display: grid;
  gap: 10px;
}

.visual-upload-card label {
  margin-bottom: 0;
  color: #3f4254;
  font-weight: 700;
}

.visual-upload-card small {
  color: #7e8299;
}

.visual-logo-preview,
.visual-cover-preview {
  display: grid;
  overflow: hidden;
  place-items: center;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.visual-logo-preview {
  width: 132px;
  height: 132px;
}

.visual-logo-preview img,
.visual-cover-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visual-logo-preview span {
  color: #f3695f;
  font-size: 36px;
  font-weight: 900;
}

.visual-cover-preview {
  min-height: 164px;
  text-align: center;
  background: linear-gradient(135deg, #172c30, #ee7361);
  color: #ffffff;
}

.visual-cover-preview div {
  display: grid;
  gap: 6px;
  padding: 18px;
}

.visual-cover-preview strong,
.visual-cover-preview span {
  display: block;
}

.visual-cover-preview strong {
  font-size: 20px;
}

.visual-cover-preview span {
  color: rgba(255, 255, 255, 0.78);
}

.visual-choice-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.visual-choice-label {
  margin-bottom: 8px;
  color: #3f4254;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.visual-choice {
  min-height: 92px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 8px;
  background: #ffffff;
  color: #3f4254;
  text-align: left;
}

.visual-choice strong,
.visual-choice span {
  display: block;
}

.visual-choice span {
  margin-top: 5px;
  color: #7e8299;
  font-size: 12px;
}

.visual-choice--active {
  border-color: #ee7361;
  background: #fff5f2;
  color: #ee7361;
}

.visual-warning,
.visual-preview-note {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff4de;
  color: #895b00;
  font-weight: 700;
}

.visual-live-preview {
  position: sticky;
  top: 0;
  align-self: start;
  overflow: hidden;
  height: 560px;
  padding: 10px;
  border: 1px solid #ebedf3;
  border-radius: 18px;
  background: #f4f5f9;
  color: #172c30;
}

.visual-live-preview__frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 12px;
  background: #ffffff;
}

.visual-live-preview__empty {
  display: grid;
  gap: 6px;
  align-content: center;
  justify-items: center;
  height: 100%;
  padding: 20px;
  color: #7e8299;
  text-align: center;
}

.visual-live-preview__empty strong {
  color: #3f4254;
}

.visual-live-preview__cover {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 124px;
  padding: 14px;
  border-radius: var(--preview-card-radius);
  background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.92),
      rgba(255, 255, 255, 0.72)
    ),
    linear-gradient(135deg, var(--preview-accent), var(--preview-primary));
  background-position: center;
  background-size: cover;
}

.visual-live-preview__logo {
  display: grid;
  overflow: hidden;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 1px solid rgba(238, 115, 97, 0.22);
  border-radius: 10px;
  background: #ffffff;
  color: var(--preview-primary);
  font-weight: 900;
}

.visual-live-preview__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.visual-live-preview__cover strong,
.visual-live-preview__cover small {
  display: block;
}

.visual-live-preview__cover strong {
  font-size: 18px;
}

.visual-live-preview__cover small {
  margin-top: 4px;
  color: #7e8299;
}

.visual-live-preview__button,
.visual-live-preview__cart {
  display: grid;
  min-height: 42px;
  place-items: center;
  border: 0;
  border-radius: var(--preview-button-radius);
  background: var(--preview-primary);
  color: #ffffff;
  font-weight: 900;
}

.visual-live-preview__card {
  display: grid;
  gap: 4px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: var(--preview-card-radius);
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(23, 44, 48, 0.08);
}

.visual-live-preview__card span {
  color: var(--preview-primary);
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.visual-live-preview__card small {
  color: #7e8299;
}

.visual-live-preview__card b {
  margin-top: 6px;
  font-size: 18px;
}

.visual-live-preview__alert {
  padding: 11px 12px;
  border: 1px solid rgba(238, 115, 97, 0.26);
  border-radius: var(--preview-card-radius);
  background: rgba(238, 115, 97, 0.08);
  color: #172c30;
  font-weight: 700;
}

.surface-list,
.category-row,
.mini-product-row,
.publishing-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.publishing-actions {
  margin-top: 14px;
}

.category-row {
  margin: 18px 0;
}

.category-chip,
.mini-product {
  padding: 10px 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
  color: #181c32;
  font-weight: 800;
}

.category-chip span,
.mini-product small {
  display: block;
  color: #7e8299;
  font-size: 12px;
  font-weight: 700;
}

.category-chip--paused {
  border-color: #ffd7dd;
  background: #fff5f6;
}

.catalog-status-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.pause-modal-body {
  display: grid;
  gap: 12px;
}

.pause-target-box {
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.pause-target-box span,
.pause-target-box strong,
.pause-target-box small {
  display: block;
}

.pause-target-box span {
  color: #7e8299;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.pause-target-box strong {
  margin: 4px 0;
  color: #181c32;
}

.pause-target-box small,
.pause-choice small {
  color: #7e8299;
}

.pause-choice {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin: 0;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.pause-choice input {
  margin-top: 3px;
}

.pause-choice strong,
.pause-choice small {
  display: block;
}

.pause-choice-active {
  border-color: #f3695f;
  background: #fff5f3;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-modal-header {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}

.product-modal-header strong,
.product-modal-header small {
  display: block;
}

.product-modal-header strong {
  color: #181c32;
  font-size: 18px;
}

.product-modal-header small {
  color: #7e8299;
}

.product-modal-header .product-photo-preview {
  grid-row: auto;
  overflow: hidden;
  flex: 0 0 68px;
}

.product-modal-header .product-photo-preview,
.product-modal-header .product-photo-preview img,
.product-modal-header .product-photo-preview span {
  width: 68px;
  height: 68px;
}

.product-modal-header .product-photo-preview img,
.product-modal-header .product-photo-preview span {
  display: grid;
  place-items: center;
}

.product-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.product-tabs button {
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  color: #3f4254;
  font-weight: 800;
}

.product-tabs .product-tab-active {
  border-color: #f3695f;
  background: #fff5f3;
  color: #f3695f;
}

.product-media-panel,
.product-config-panel {
  display: grid;
  gap: 18px;
}

.product-media-panel {
  grid-template-columns: 260px minmax(0, 1fr);
  align-items: start;
}

.guided-option-panel {
  display: grid;
  gap: 14px;
  padding: 16px;
  border: 1px solid #ffe1db;
  border-radius: 6px;
  background: #fff8f6;
}

.guided-option-panel strong,
.guided-option-panel p {
  display: block;
  margin: 0;
}

.guided-option-panel p {
  color: #7e8299;
}

.guided-option-grid,
.option-preset-panel {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.guided-option-grid button,
.option-preset-panel button {
  min-height: 74px;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.guided-option-grid button:hover,
.option-preset-panel button:hover,
.option-preset-active {
  border-color: #f3695f !important;
  background: #fff5f3 !important;
}

.guided-option-grid strong,
.guided-option-grid span,
.option-preset-panel strong,
.option-preset-panel span {
  display: block;
}

.guided-option-grid strong,
.option-preset-panel strong {
  color: #181c32;
}

.guided-option-grid span,
.option-preset-panel span {
  margin-top: 4px;
  color: #7e8299;
  font-size: 12px;
}

.option-scope-guide {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.option-scope-guide article {
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-scope-guide span,
.option-scope-guide strong,
.option-scope-guide p {
  display: block;
}

.option-scope-guide span {
  color: #f3695f;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.option-scope-guide strong {
  margin-top: 4px;
  color: #181c32;
}

.option-scope-guide p {
  margin: 6px 0 0;
  color: #7e8299;
}

.product-linked-list {
  display: grid;
  gap: 10px;
}

.product-linked-list article {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.product-linked-list strong,
.product-linked-list small {
  display: block;
}

.product-linked-list small {
  color: #7e8299;
}

.toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.promotion-preset-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.promotion-preset-grid button {
  min-height: 86px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.promotion-preset-grid button:hover,
.promotion-preset-active {
  border-color: #f3695f !important;
  background: #fff5f3 !important;
}

.promotion-preset-grid strong,
.promotion-preset-grid span {
  display: block;
}

.promotion-preset-grid span {
  margin-top: 5px;
  color: #7e8299;
  font-size: 12px;
}

.promotion-image-row {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.promotion-image-preview {
  display: grid;
  width: 100%;
  min-height: 124px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: linear-gradient(135deg, #fff5f3, #f8f9fb);
  color: #f3695f;
  font-weight: 900;
  text-align: center;
}

.promotion-image-preview img {
  width: 100%;
  height: 124px;
  object-fit: cover;
}

.commercial-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.commercial-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 132px;
  padding: 18px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.commercial-card-featured {
  border-color: #ffd4cc;
  background: #fff5f3;
}

.commercial-card span,
.commercial-card strong,
.commercial-card small {
  display: block;
}

.commercial-card span {
  color: #7e8299;
  font-weight: 800;
}

.commercial-card strong {
  margin: 6px 0;
  color: #181c32;
  font-size: 28px;
  font-weight: 900;
}

.commercial-card small,
.commercial-row small {
  color: #7e8299;
}

.commercial-list-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.commercial-list-wide {
  grid-column: span 2;
}

.commercial-list {
  display: grid;
  align-content: start;
  gap: 10px;
}

.commercial-list h5 {
  margin: 0;
  color: #181c32;
  font-weight: 900;
}

.commercial-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.commercial-row-with-image {
  grid-template-columns: auto minmax(0, 1fr) auto auto;
}

.commercial-row-image {
  width: 64px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
}

.commercial-row strong,
.commercial-row small {
  display: block;
}

.weekday-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.weekday-picker label {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.highlight-toggle-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  cursor: pointer;
}

.highlight-toggle-card input {
  margin-top: 4px;
}

.highlight-toggle-card strong,
.highlight-toggle-card small {
  display: block;
}

.highlight-toggle-card small {
  color: #7e8299;
}

.product-cell > span,
.mini-product span,
.product-photo-preview span {
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: #fff5f3;
  color: #f3695f;
  font-weight: 900;
}

.product-cell > span,
.product-cell img {
  width: 48px;
  height: 48px;
}

.product-photo-preview-lg,
.product-photo-preview-lg img,
.product-photo-preview-lg span {
  width: 240px;
  height: 180px;
}

.product-cell img,
.mini-product img,
.product-photo-preview img {
  border-radius: 6px;
  object-fit: cover;
}

.product-cell img {
  flex: 0 0 48px;
}

.product-cell strong,
.product-cell small {
  display: block;
}

.product-cell small {
  max-width: 360px;
  color: #7e8299;
  white-space: normal;
}

.mini-product {
  display: grid;
  width: 160px;
  min-height: 172px;
  gap: 8px;
  text-align: left;
}

.mini-product img,
.mini-product span {
  width: 100%;
  height: 96px;
}

.option-admin-list {
  display: grid;
  gap: 12px;
}

.option-admin-card {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(280px, 2fr) auto;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-admin-card strong,
.option-admin-card span,
.option-admin-card small {
  display: block;
}

.option-admin-card small {
  color: #7e8299;
}

.option-admin-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.option-editor {
  display: grid;
  gap: 12px;
}

.option-editor-row {
  display: grid;
  grid-template-columns:
    minmax(130px, 1fr) minmax(160px, 1.2fr) 110px minmax(150px, 1fr) auto
    auto;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
}

.option-editor-row-wide {
  grid-template-columns:
    minmax(120px, 1fr) minmax(120px, 1fr) minmax(160px, 1.2fr) 100px
    minmax(150px, 1fr) minmax(140px, 1fr) auto auto;
}

.advanced-section-note {
  display: grid;
  gap: 4px;
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.advanced-section-note strong,
.advanced-section-note span {
  display: block;
}

.advanced-section-note span {
  color: #7e8299;
}

.delivery-zones {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #ebedf3;
}

.delivery-zones__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.delivery-zones__head strong {
  display: block;
}

.delivery-zones__head small {
  display: block;
  color: #7e8299;
}

.delivery-zones__empty {
  margin: 0 0 12px;
  color: #7e8299;
  font-size: 0.9rem;
}

.delivery-zones__list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delivery-zones__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #ebedf3;
  border-radius: 8px;
}

.delivery-zones__info strong {
  display: block;
}

.delivery-zones__info small {
  display: block;
  color: #7e8299;
}

.delivery-zones__actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.delivery-zones__form {
  margin-top: 4px;
  padding: 14px;
  border: 1px dashed #c9cdd6;
  border-radius: 10px;
}

.delivery-zones__form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.option-preset-panel {
  margin-bottom: 18px;
}

.option-category-picker {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #f8f9fb;
}

.option-product-picker {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.option-product-picker button {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 10px;
  align-items: center;
  padding: 12px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  background: #ffffff;
  text-align: left;
}

.option-product-picker button:hover {
  border-color: #f3695f;
  background: #fff5f3;
}

.option-product-picker img,
.option-product-picker span {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
}

.option-product-picker span {
  display: grid;
  place-items: center;
  background: #fff1ef;
  color: #f3695f;
  font-weight: 800;
}

.option-product-picker strong,
.option-product-picker small {
  display: block;
}

.option-product-picker small {
  color: #7e8299;
}

.option-advanced-link {
  color: #7e8299;
  font-weight: 700;
}

.option-advanced-link summary {
  cursor: pointer;
}

.option-advanced-link select {
  margin-top: 8px;
}

.checkbox-line {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
  color: #3f4254;
  font-weight: 600;
}

.control-card ul {
  margin: 14px 0;
  padding-left: 18px;
}

.modal-control-grid,
.product-modal-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.product-modal-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.modal-control-field,
.modal-control-field label,
.modal-control-field small {
  display: block;
}

.modal-control-field label {
  color: #181c32;
  font-weight: 800;
}

.modal-control-field small {
  margin-top: 6px;
  color: #7e8299;
}

.modal-control-field-wide {
  grid-column: span 2;
}

.cardapio-map-preview {
  width: 100%;
  min-height: 260px;
  border: 1px solid #e4e6ef;
  border-radius: 8px;
  background: #f3f6f9;
}

.product-photo-preview {
  grid-row: span 3;
}

.product-photo-preview img,
.product-photo-preview span {
  width: 100%;
  height: 220px;
}

.loading-box {
  padding: 32px;
  border: 1px dashed #d7dae7;
  border-radius: 6px;
  color: #7e8299;
  text-align: center;
}

@media (max-width: 1199px) {
  .overview-grid,
  .content-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .overview-grid,
  .content-grid,
  .modal-control-grid,
  .product-modal-grid,
  .visual-editor,
  .visual-choice-grid {
    grid-template-columns: 1fr;
  }

  .control-card-wide,
  .modal-control-field-wide {
    grid-column: span 1;
  }

  .section-header,
  .toolbar-line,
  .brand-preview {
    display: block;
  }

  .product-photo-preview {
    grid-row: span 1;
  }
}
</style>
